import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, Download, Check, X, Trash2, Pencil, FileText, Filter, Send, PenTool, Mail, Wrench, FileDown, Ticket, Clock, CalendarDays, ArrowUpDown, ArrowUp, ArrowDown, Building2, Users, Camera, ChevronLeft, ChevronRight, ZoomIn, Copy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import MultiDayReportForm from '../components/MultiDayReportForm';
import PhotoUpload, { uploadPendingPhotos } from '../components/PhotoUpload';
import { formatDateIT } from '../utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_ACTIVITY_TYPES = [
  'Altro', 'Derattizzazione', 'Disinfestazione', 'Pulizia ordinaria', 
  'Pulizia straordinaria', 'Pulizia vetri', 'Sanificazione', 'Trattamento pavimenti'
];

const EQUIPMENT_LIST = [
  "Aspiraliquidi/polvere", "Generatore di Ozono", "Generatore di Perossido di Ossigeno", 
  "Generatore di Vapore", "Idropulitrice a caldo/freddo", "Lavasciuga uomo a bordo",
  "Lavasciuga uomo a terra", "Monospazzola", "Spazzatrice"
];

const formatHours = (hours) => Number(hours || 0).toFixed(2);

// Formatta data di creazione (created_at in formato ISO)
const formatCreatedAt = (createdAt) => {
  if (!createdAt) return '-';
  try {
    const date = new Date(createdAt);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '-';
  }
};

const calculateHoursBetween = (start, end) => {
  if (!start || !end) return 0;
  const [startH, startM] = start.split(':').map(Number);
  const [endH, endM] = end.split(':').map(Number);
  
  let startMinutes = startH * 60 + startM;
  let endMinutes = endH * 60 + endM;
  
  // Se l'orario di fine è minore o uguale a quello di inizio, significa che finisce il giorno dopo
  if (endMinutes <= startMinutes) {
    endMinutes += 24 * 60; // Aggiungi 24 ore
  }
  
  return (endMinutes - startMinutes) / 60;
};

// Signature Pad Component
const SignaturePad = ({ onSave, onClear, savedSignature, label }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);

  // Block body scroll when zoomed overlay is open
  useEffect(() => {
    if (isZoomed) {
      // Save current scroll position and lock body
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore scroll position when closing
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isZoomed]);

  useEffect(() => {
    // Small delay to ensure canvas is mounted in DOM (especially for portal)
    const initCanvas = () => {
      if (canvasRef.current && !savedSignature) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.strokeStyle = '#1B6B93';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
      }
    };
    
    if (isZoomed) {
      // For zoomed view, wait for portal to mount
      setTimeout(initCanvas, 50);
    } else {
      initCanvas();
    }
  }, [savedSignature, isZoomed]);

  const getCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDrawing = (e) => { 
    e.preventDefault(); 
    e.stopPropagation();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d'); 
    const coords = getCoords(e); 
    ctx.beginPath(); 
    ctx.moveTo(coords.x, coords.y); 
    setIsDrawing(true); 
  };
  const draw = (e) => { 
    if (!isDrawing) return; 
    e.preventDefault(); 
    e.stopPropagation();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d'); 
    const coords = getCoords(e); 
    ctx.lineTo(coords.x, coords.y); 
    ctx.stroke(); 
  };
  const stopDrawing = (e) => { 
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsDrawing(false); 
  };
  const clearCanvas = () => { 
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d'); 
    ctx.fillStyle = '#ffffff'; 
    ctx.fillRect(0, 0, canvas.width, canvas.height); 
  };
  const saveSignature = () => { 
    const canvas = canvasRef.current;
    if (!canvas) {
      toast.error('Errore: canvas non disponibile');
      return;
    }
    try {
      const dataUrl = canvas.toDataURL('image/png');
      if (!dataUrl || dataUrl === 'data:,') {
        toast.error('Errore nel salvataggio della firma');
        return;
      }
      onSave(dataUrl); 
      setIsZoomed(false); 
      toast.success('Firma salvata!'); 
    } catch (err) {
      console.error('Error saving signature:', err);
      toast.error('Errore nel salvataggio della firma');
    }
  };
  const cancelZoom = () => { setIsZoomed(false); };

  if (savedSignature) {
    return (
      <div className="space-y-2">
        <Label className="flex items-center gap-2 text-emerald-600"><Check className="w-4 h-4" /> {label} - Firmato</Label>
        <div className="border rounded-lg p-2 bg-emerald-50"><img src={savedSignature} alt="Firma" className="max-h-20 mx-auto" /></div>
        <Button type="button" variant="outline" size="sm" onClick={onClear}>Cancella e Rifirma</Button>
      </div>
    );
  }

  // Zoomed mode - full screen overlay for mobile (using portal to escape Dialog z-index)
  const zoomedOverlay = isZoomed ? ReactDOM.createPortal(
    <>
      {/* Dark overlay background */}
      <div 
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          cancelZoom();
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          e.stopPropagation();
          cancelZoom();
        }}
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.92)',
          zIndex: 999998
        }}
      />
      {/* Signature card - centered (higher z-index to receive events) */}
      <div 
        onClick={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        className="bg-white rounded-xl p-4 shadow-2xl"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'calc(100% - 2rem)',
          maxWidth: '28rem',
          zIndex: 999999,
          pointerEvents: 'auto'
        }}
      >
        <Label className="flex items-center gap-2 mb-3 text-lg font-semibold"><PenTool className="w-5 h-5 text-[#1B6B93]" /> {label}</Label>
        <div className="border-2 border-[#1B6B93] rounded-lg p-2 bg-white">
          <canvas 
            ref={canvasRef} 
            width={500} 
            height={200} 
            className="w-full cursor-crosshair" 
            style={{height: '200px', touchAction: 'none'}}
            onMouseDown={startDrawing} 
            onMouseMove={draw} 
            onMouseUp={stopDrawing} 
            onMouseLeave={stopDrawing} 
            onTouchStart={startDrawing} 
            onTouchMove={draw} 
            onTouchEnd={stopDrawing} 
          />
        </div>
        <p className="text-xs text-slate-500 mt-2 text-center">Firma con il dito o il mouse nell'area sopra</p>
        <div className="flex gap-2 mt-4">
          <Button type="button" variant="outline" className="flex-1" onClick={clearCanvas}>Cancella</Button>
          <Button type="button" variant="outline" className="flex-1" onClick={cancelZoom}>Annulla</Button>
          <Button type="button" className="flex-1 btn-primary" onClick={saveSignature}><Check className="w-4 h-4 mr-1" /> Conferma</Button>
        </div>
      </div>
    </>,
    document.body
  ) : null;

  // Normal mode - clickable placeholder on mobile, direct canvas on desktop
  return (
    <>
      {zoomedOverlay}
      <div className="space-y-2">
        <Label className="flex items-center gap-2"><PenTool className="w-4 h-4" /> {label} *</Label>
        {/* Mobile: Click to open zoomed view */}
        <div 
          className="md:hidden border-2 border-dashed border-slate-300 rounded-lg p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
          onClick={() => setIsZoomed(true)}
        >
          <div className="flex flex-col items-center justify-center text-slate-500">
            <PenTool className="w-8 h-8 mb-2" />
            <span className="text-sm font-medium">Tocca per firmare</span>
            <span className="text-xs">(si aprirà un'area più grande)</span>
          </div>
        </div>
        {/* Desktop: Direct canvas */}
        <div className="hidden md:block border-2 border-dashed border-slate-300 rounded-lg p-2 bg-white">
          <canvas ref={canvasRef} width={400} height={120} className="w-full cursor-crosshair touch-none" onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} />
        </div>
        <div className="hidden md:flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={clearCanvas}>Cancella</Button>
          <Button type="button" size="sm" onClick={saveSignature} className="btn-secondary"><Check className="w-4 h-4 mr-1" /> Conferma</Button>
        </div>
      </div>
    </>
  );
};

const ReportsPage = () => {
  const { user, isAdmin, isAdminOrGestore } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [sites, setSites] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityTypes, setActivityTypes] = useState(DEFAULT_ACTIVITY_TYPES);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [correctingReport, setCorrectingReport] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingApproval, setSendingApproval] = useState(false);
  const [selectedReportForEmail, setSelectedReportForEmail] = useState(null);
  const [emailNoHours, setEmailNoHours] = useState(null);
  const [selectedReportForApproval, setSelectedReportForApproval] = useState(null);
  const [approvalLink, setApprovalLink] = useState('');
  const [useIndividualTimes, setUseIndividualTimes] = useState(false);
  const [isMultiDayDialogOpen, setIsMultiDayDialogOpen] = useState(false);
  
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [filterSite, setFilterSite] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  
  // Sorting state
  const [sortField, setSortField] = useState('report_number'); // 'report_number', 'date', 'site_name'
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'

  // Handle URL params to auto-open dialogs
  useEffect(() => {
    const newParam = searchParams.get('new');
    if (newParam === 'single') {
      // Precompila il firmatario dipendente con l'utente loggato
      setFormData(prev => ({
        ...prev,
        employee_signer_name: user?.name || ''
      }));
      setIsDialogOpen(true);
      setSearchParams({});
    } else if (newParam === 'multi') {
      setIsMultiDayDialogOpen(true);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, user]);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    start_time: '08:00',
    end_time: '10:00',
    hours_per_employee: 2,
    total_hours: 2,
    activity_type: '',
    site_id: '',
    ticket: '',
    ticket_id: '', // ID del ticket dall'app Ticket (se collegato)
    notes: '',
    employees: [],
    equipment: [],
    client_email: '',
    employee_signer_name: '',
    client_signer_name: '',
    employee_signature: '',
    client_signature: '',
    client_report_no_hours: false
  });

  const [emailFormData, setEmailFormData] = useState({ client_email: '', client_signer_name: '', client_signature: '' });
  const [sendApprovalOnCreate, setSendApprovalOnCreate] = useState(false);
  const [skipEmailSend, setSkipEmailSend] = useState(false); // Solo admin - per rapporti già consegnati
  
  // Stato per visualizzazione dettaglio rapporto
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedReportDetail, setSelectedReportDetail] = useState(null);
  const [reminderHistory, setReminderHistory] = useState(null);
  
  // Stato per diagnostica rapporto
  const [diagnosisData, setDiagnosisData] = useState(null);
  const [showDiagnosis, setShowDiagnosis] = useState(false);
  const [loadingDiagnosis, setLoadingDiagnosis] = useState(false);
  
  // Stato per aggiunta nuovo cantiere
  const [showNewSiteForm, setShowNewSiteForm] = useState(false);
  const [newSiteData, setNewSiteData] = useState({ name: '', address: '', client_name: '', client_email: '' });
  const [creatingSite, setCreatingSite] = useState(false);
  // Stato per i valori di input delle ore attrezzature (stringhe per permettere virgola/punto)
  const [equipmentHoursInput, setEquipmentHoursInput] = useState({});
  // Lista attrezzature dal server (fallback: EQUIPMENT_LIST)
  const [equipmentOptions, setEquipmentOptions] = useState(EQUIPMENT_LIST);
  // Stato per foto in attesa di upload (nuovi rapporti)
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, status: 'idle' });
  // Stato per visualizzazione foto di un rapporto
  const [viewingPhotosReport, setViewingPhotosReport] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  // Stato per ticket ricevuti dall'app Ticket
  const [pendingTickets, setPendingTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  // Straordinari pianificati ricevuti dal portale HR
  const [hrPlannedJobs, setHrPlannedJobs] = useState([]);
  const [showPlannedJobs, setShowPlannedJobs] = useState(false);
  const convertingJobRef = useRef(null);
  const [sendingToHr, setSendingToHr] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [reportsRes, sitesRes, employeesRes, ticketsRes, actTypesRes, equipmentRes, hrJobsRes] = await Promise.all([
        axios.get(`${API}/reports`), 
        axios.get(`${API}/sites${isAdminOrGestore() ? '/all' : ''}`), 
        axios.get(`${API}/employees`),
        axios.get(`${API}/integrations/tickets`).catch(() => ({ data: [] })),
        axios.get(`${API}/settings/activity-types`).catch(() => ({ data: { types: DEFAULT_ACTIVITY_TYPES } })),
        axios.get(`${API}/equipment`).catch(() => ({ data: { equipment: EQUIPMENT_LIST } })),
        axios.get(`${API}/integrations/hr/planned-jobs-list`).catch(() => ({ data: [] }))
      ]);
      setReports(reportsRes.data);
      setSites(sitesRes.data);
      // Filter only operatives and workers (exclude admin and gestore)
      // L'ordinamento alfabetico per cognome è già fatto dal backend
      const filteredEmployees = employeesRes.data
        .filter(e => e.role !== 'admin' && e.role !== 'gestore');
      setEmployees(filteredEmployees);
      setPendingTickets(ticketsRes.data || []);
      setActivityTypes(actTypesRes.data?.types || DEFAULT_ACTIVITY_TYPES);
      const eqList = equipmentRes.data?.equipment || EQUIPMENT_LIST;
      setEquipmentOptions([...eqList].sort((a, b) => a.localeCompare(b, 'it')));
      setHrPlannedJobs(Array.isArray(hrJobsRes.data) ? hrJobsRes.data : []);
      console.log('[ReportsPage] Loaded tickets:', ticketsRes.data?.length || 0, ticketsRes.data);
    } catch (error) { 
      console.error('[ReportsPage] fetchData error:', error);
      toast.error('Errore nel caricamento'); 
    }
    finally { setLoading(false); }
  };

  const recalculateTotalHours = (empList, defaultHours) => {
    if (empList.length === 0) return defaultHours;
    const hasIndividual = empList.some(e => e.hours != null);
    if (hasIndividual) {
      return empList.reduce((sum, e) => sum + (e.hours || defaultHours), 0);
    }
    return defaultHours * empList.length;
  };

  const handleTimeChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    if (field === 'start_time' || field === 'end_time') {
      const hoursPerEmp = calculateHoursBetween(
        field === 'start_time' ? value : formData.start_time,
        field === 'end_time' ? value : formData.end_time
      );
      newData.hours_per_employee = hoursPerEmp;
      // Update employees without individual times
      newData.employees = newData.employees.map(emp => {
        if (!emp.start_time && !emp.end_time) {
          return { ...emp, hours: hoursPerEmp };
        }
        return emp;
      });
      newData.total_hours = recalculateTotalHours(newData.employees, hoursPerEmp);
    }
    setFormData(newData);
  };

  const handleAddEmployee = (employeeId) => {
    if (!employeeId || employeeId === 'none') return;
    const emp = employees.find(e => e.id === employeeId);
    if (!emp || formData.employees.some(e => e.employee_id === employeeId)) return;
    const newEmp = { 
      employee_id: emp.id, 
      employee_name: emp.name, 
      employee_matricola: emp.matricola,
      start_time: useIndividualTimes ? formData.start_time : null,
      end_time: useIndividualTimes ? formData.end_time : null,
      hours: formData.hours_per_employee
    };
    const newEmployees = [...formData.employees, newEmp];
    setFormData({ ...formData, employees: newEmployees, total_hours: recalculateTotalHours(newEmployees, formData.hours_per_employee) });
  };

  const handleRemoveEmployee = (employeeId) => {
    const newEmployees = formData.employees.filter(e => e.employee_id !== employeeId);
    setFormData({ ...formData, employees: newEmployees, total_hours: recalculateTotalHours(newEmployees, formData.hours_per_employee) });
  };

  const handleEmployeeTimeChange = (employeeId, field, value) => {
    const newEmployees = formData.employees.map(emp => {
      if (emp.employee_id === employeeId) {
        const updated = { ...emp, [field]: value };
        if (field === 'start_time' || field === 'end_time') {
          updated.hours = calculateHoursBetween(
            field === 'start_time' ? value : emp.start_time,
            field === 'end_time' ? value : emp.end_time
          );
        }
        return updated;
      }
      return emp;
    });
    setFormData({ ...formData, employees: newEmployees, total_hours: recalculateTotalHours(newEmployees, formData.hours_per_employee) });
  };

  const handleAddEquipment = (equipName) => {
    if (!equipName || equipName === 'none') return;
    // Inizializza con gli orari del rapporto (permetti duplicati)
    setFormData({ 
      ...formData, 
      equipment: [...formData.equipment, { 
        equipment_name: equipName, 
        start_time: formData.start_time || '', 
        end_time: formData.end_time || '' 
      }] 
    });
  };

  const handleRemoveEquipment = (index) => {
    setFormData({ ...formData, equipment: formData.equipment.filter((_, i) => i !== index) });
  };

  const handleEquipmentHoursChange = (index, hours) => {
    // Fix: gestire correttamente il valore vuoto senza lasciare lo 0
    const numericValue = hours === '' ? 0 : parseFloat(hours) || 0;
    setFormData({ ...formData, equipment: formData.equipment.map((e, i) => i === index ? { ...e, hours: numericValue } : e) });
  };

  const handleSiteChange = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    setFormData({ ...formData, site_id: siteId, client_email: site?.client_email || formData.client_email });
  };

  const handleCreateSite = async () => {
    if (!newSiteData.name.trim()) {
      toast.error('Nome cantiere obbligatorio');
      return;
    }
    setCreatingSite(true);
    try {
      const response = await axios.post(`${API}/sites`, newSiteData);
      const newSite = response.data;
      setSites([...sites, newSite].sort((a, b) => a.name.localeCompare(b.name, 'it')));
      setFormData({ ...formData, site_id: newSite.id, client_email: newSite.client_email || '' });
      setShowNewSiteForm(false);
      setNewSiteData({ name: '', address: '', client_name: '', client_email: '' });
      toast.success('Cantiere creato!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setCreatingSite(false);
    }
  };

  const handleSubmit = async (e, saveAsDraft = false) => {
    e?.preventDefault();
    
    // Guardia anti doppio click
    if (submittingRef.current) return;
    
    // Se salvataggio come bozza, validazione minima
    if (saveAsDraft) {
      if (!formData.site_id) { toast.error('Seleziona un cantiere'); return; }
      if (!formData.activity_type) { toast.error('Seleziona attività'); return; }
    } else {
      // Per dipendenti: tutte le firme obbligatorie
      // Per admin/gestore: firme opzionali (possono inviare per approvazione)
      if (!isAdminOrGestore() && !correctingReport) {
        if (!formData.employee_signer_name) { toast.error('Nome dipendente firmatario obbligatorio'); return; }
        if (!formData.employee_signature) { toast.error('Firma dipendente obbligatoria'); return; }
        if (!formData.client_signer_name) { toast.error('Nome cliente firmatario obbligatorio'); return; }
        if (!formData.client_signature) { toast.error('Firma cliente obbligatoria'); return; }
      }
      
      // Se si vuole inviare approvazione, serve l'email cliente
      if (sendApprovalOnCreate && !formData.client_email) { 
        toast.error('Email cliente obbligatoria per inviare richiesta approvazione'); 
        return; 
      }
      
      // Se entrambe le firme presenti (invio automatico email finale), serve l'email cliente
      if (formData.employee_signature && formData.client_signature && !formData.client_email && !skipEmailSend) {
        toast.error('Email cliente obbligatoria per inviare il rapporto firmato');
        return;
      }
    }
    
    try {
      submittingRef.current = true;
      setIsSubmitting(true);
      let createdReport = null;
      // Aggiungi skip_email_send e send_approval_email al payload
      const payload = { ...formData };
      if (user?.role === 'admin' && skipEmailSend) {
        payload.skip_email_send = true;
      }
      // Imposta send_approval_email se checkbox attivo e nessuna firma cliente
      if (sendApprovalOnCreate && !formData.client_signature) {
        payload.send_approval_email = true;
      }
      // Imposta is_draft se salvataggio come bozza
      if (saveAsDraft) {
        payload.is_draft = true;
      }
      
      if (editingReport) {
        if (correctingReport) {
          const res = await axios.put(`${API}/reports/${editingReport.id}/correct`, payload);
          toast.success(res.data.message || 'Rapporto corretto! PDF rigenerato e email reinviata.');
        } else {
          await axios.put(`${API}/reports/${editingReport.id}`, payload);
          toast.success('Rapporto aggiornato!');
        }
      } else {
        // Se ci sono foto pendenti, ritarda l'invio dell'email
        if (pendingPhotos.length > 0) {
          payload.delay_email_for_photos = true;
        }
        
        const response = await axios.post(`${API}/reports`, payload);
        createdReport = response.data;
        toast.success('Rapporto creato!');
        
        // Collega il rapporto al lavoro pianificato HR (se creato da lì)
        if (convertingJobRef.current && createdReport?.id) {
          const jobId = convertingJobRef.current;
          convertingJobRef.current = null;
          axios.post(`${API}/integrations/hr/planned-jobs/${jobId}/mark-converted`, { report_id: createdReport.id }).catch(() => {});
        }
        
        // Upload delle foto pendenti dopo la creazione del rapporto
        if (pendingPhotos.length > 0 && createdReport?.id) {
          setIsUploadingPhotos(true);
          setUploadProgress({ current: 0, total: pendingPhotos.length, status: 'uploading' });
          
          // Scroll al top del dialog per mostrare l'overlay su mobile
          const dialogContent = document.querySelector('[data-testid="report-dialog-content"]');
          if (dialogContent) {
            dialogContent.scrollTo({ top: 0, behavior: 'smooth' });
          }
          
          toast.info(`Caricamento ${pendingPhotos.length} foto in corso... Non chiudere la pagina!`);
          
          // Callback per mostrare progresso
          const onProgress = (progress) => {
            setUploadProgress({ current: progress.current, total: progress.total, status: progress.status });
            if (progress.status === 'completed') {
              setIsUploadingPhotos(false);
              if (progress.failed > 0) {
                toast.warning(`${progress.uploaded}/${progress.total} foto caricate. ${progress.failed} fallite.`);
                progress.failedFiles?.forEach(f => {
                  console.error(`Foto non caricata: ${f.filename} - ${f.error}`);
                });
              }
            }
          };
          
          const uploaded = await uploadPendingPhotos(createdReport.id, pendingPhotos, onProgress);
          setIsUploadingPhotos(false);
          
          if (uploaded.length > 0) {
            if (uploaded.length === pendingPhotos.length) {
              toast.success(`${uploaded.length} foto caricate con successo!`);
            }
          } else if (pendingPhotos.length > 0) {
            toast.error('Nessuna foto caricata. Puoi riprovare dalla modifica del rapporto.');
          }
          
          // SOLO dopo upload foto, triggera l'invio dell'email
          // Questo endpoint viene chiamato SOLO quando c'erano foto da caricare
          // (l'email è stata ritardata con delay_email_for_photos=true)
          if (createdReport?.id && !skipEmailSend && formData.employee_signature) {
            try {
              console.log('[ReportsPage] Triggering send-email-after-photos for report', createdReport.id);
              await axios.post(`${API}/reports/${createdReport.id}/send-email-after-photos`);
              if (formData.client_signature) {
                toast.success('Email con PDF in invio!');
              } else if (sendApprovalOnCreate) {
                toast.success('Email di approvazione in invio!');
              }
            } catch (emailError) {
              console.error('Error triggering email:', emailError);
              // Non mostrare errore se l'email è già stata inviata o se manca email
              if (emailError.response?.data?.detail && !emailError.response.data.detail.includes('già')) {
                toast.error('Errore invio email: ' + emailError.response.data.detail);
              }
            }
          }
        }
        
        // Notifica se email di approvazione sarà inviata automaticamente (senza foto)
        if (!skipEmailSend && sendApprovalOnCreate && !formData.client_signature && createdReport?.id && pendingPhotos.length === 0) {
          toast.success('Email di approvazione in invio al cliente!');
        }
        
        // Notifica se email finale sarà inviata automaticamente (senza foto, con entrambe le firme)
        if (!skipEmailSend && formData.employee_signature && formData.client_signature && pendingPhotos.length === 0) {
          toast.success('Email con PDF in invio!');
        }
      }
      setIsDialogOpen(false); setEditingReport(null); setCorrectingReport(false); resetForm(); fetchData();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.warning('Rapporto già inviato. Attendi qualche secondo prima di riprovare.');
      } else {
        toast.error(error.response?.data?.detail || 'Errore');
      }
    } finally { submittingRef.current = false; setIsSubmitting(false); }
  };

  const resetForm = () => {
    setFormData({ 
      date: new Date().toISOString().split('T')[0], 
      start_time: '08:00', 
      end_time: '10:00', 
      hours_per_employee: 2, 
      total_hours: 2, 
      activity_type: '', 
      site_id: '', 
      ticket: '', 
      ticket_id: '', // Reset ticket collegato
      notes: '', 
      employees: [], 
      equipment: [], 
      client_email: '', 
      employee_signer_name: user?.name || '', // Precompila con l'utente loggato
      client_signer_name: '', 
      employee_signature: '', 
      client_signature: '' 
    });
    setUseIndividualTimes(false);
    setSendApprovalOnCreate(false);
    setSkipEmailSend(false);
    setPendingPhotos([]); // Reset foto pendenti
    setSelectedTicket(null); // Reset ticket selezionato
  };

  const handleEdit = (report) => {
    setEditingReport(report);
    
    // Se è un rapporto multi-giorno, apri il form multi-giorno
    if (report.is_multi_day) {
      setIsMultiDayDialogOpen(true);
    } else {
      // Rapporto singolo
      const hasIndividualTimes = report.employees?.some(e => e.start_time || e.end_time);
      setUseIndividualTimes(hasIndividualTimes);
      setFormData({ ...report, hours_per_employee: report.hours_per_employee || report.total_hours, employees: report.employees || [], equipment: report.equipment || [], ticket: report.ticket || '', notes: report.notes || '', client_email: report.client_email || '', employee_signer_name: report.employee_signer_name || '', client_signer_name: report.client_signer_name || '', employee_signature: report.employee_signature || '', client_signature: report.client_signature || '' });
      setIsDialogOpen(true);
    }
  };

  const handleDelete = async (id) => { if (!window.confirm('Eliminare?')) return; try { await axios.delete(`${API}/reports/${id}`); toast.success('Eliminato!'); fetchData(); } catch { toast.error('Errore'); } };
  const handleApprove = async (id) => { try { await axios.put(`${API}/reports/${id}/approve`); toast.success('Approvato!'); fetchData(); } catch { toast.error('Errore'); } };
  const handleReject = async (id) => { try { await axios.put(`${API}/reports/${id}/reject`); toast.success('Rifiutato!'); fetchData(); } catch { toast.error('Errore'); } };
  const handleCorrectReport = (report) => {
    setCorrectingReport(true);
    handleEdit(report);
  };
  
  const handleConfirmDraft = async (id) => {
    try {
      const response = await axios.put(`${API}/reports/${id}/confirm-draft`);
      toast.success(response.data.message || 'Bozza confermata!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella conferma');
    }
  };

  // Duplica un rapporto esistente (solo admin/gestore)
  const handleDuplicate = (report) => {
    if (!window.confirm(`Duplicare il rapporto ${report.report_number || 'BOZZA'}?\n\nVerrà aperto il form di creazione con i dati precompilati.`)) return;
    
    if (report.is_multi_day) {
      // Duplica rapporto multi-giorno
      setIsMultiDayDialogOpen(true);
      // Passa i dati al form multi-giorno tramite uno stato dedicato
      setDuplicateReportData({
        site_id: report.site_id,
        activity_type: report.activity_type,
        ticket: report.ticket || '',
        notes: report.notes || '',
        workdays: report.workdays?.map(wd => ({
          date: '', // Lascia vuota la data per permettere la modifica
          start_time: wd.start_time,
          end_time: wd.end_time,
          employees: wd.employees?.map(e => ({
            employee_id: e.employee_id,
            employee_name: e.employee_name,
            employee_matricola: e.employee_matricola,
            start_time: e.start_time,
            end_time: e.end_time
          })) || [],
          equipment: wd.equipment?.map(eq => ({
            equipment_name: eq.equipment_name,
            start_time: eq.start_time,
            end_time: eq.end_time
          })) || []
        })) || [],
        client_report_no_hours: report.client_report_no_hours || false
      });
      toast.info('Compila le date e modifica i dati come necessario');
    } else {
      // Duplica rapporto singolo
      setFormData({
        ...formData,
        site_id: report.site_id,
        activity_type: report.activity_type,
        date: '', // Lascia vuota la data
        start_time: report.start_time,
        end_time: report.end_time,
        employees: report.employees?.map(e => ({
          employee_id: e.employee_id,
          employee_name: e.employee_name,
          employee_matricola: e.employee_matricola,
          start_time: e.start_time,
          end_time: e.end_time
        })) || [],
        equipment: report.equipment?.map(eq => ({
          equipment_name: eq.equipment_name,
          start_time: eq.start_time,
          end_time: eq.end_time
        })) || [],
        ticket: report.ticket || '',
        notes: report.notes || '',
        client_report_no_hours: report.client_report_no_hours || false
      });
      setIsDialogOpen(true);
      toast.info('Compila la data e modifica i dati come necessario');
    }
  };

  // Stato per dati duplicati (passati al form multi-giorno)
  const [duplicateReportData, setDuplicateReportData] = useState(null);

  const handleDownloadPDF = async (reportId, noHours = null) => {
    try {
      const params = noHours !== null ? `?no_hours=${noHours}` : '';
      const response = await axios.get(`${API}/reports/${reportId}/download-pdf${params}`, { responseType: 'blob' });
      
      // Estrai il nome del file dall'header Content-Disposition
      const contentDisposition = response.headers['content-disposition'];
      let filename = `rapporto_${reportId}.pdf`; // fallback
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success('PDF scaricato!');
    } catch { toast.error('Errore download'); }
  };

  // Apri dettaglio rapporto
  const createFromPlannedJob = (job) => {
    const site = activeSites.find(s => (s.name || '').toLowerCase() === (job.worksite_name || '').toLowerCase());
    const emp = employees.find(u => (u.matricola || '') === job.matricola);
    const start = job.time_from || '08:00';
    const end = job.time_to || '10:00';
    const hrs = job.hours || 0;
    setFormData({
      date: job.date || new Date().toISOString().split('T')[0],
      start_time: start,
      end_time: end,
      hours_per_employee: hrs,
      total_hours: hrs,
      activity_type: '',
      site_id: site?.id || '',
      ticket: '',
      ticket_id: '',
      notes: job.description || '',
      employees: emp ? [{ employee_id: emp.id, employee_name: emp.name, employee_matricola: emp.matricola, start_time: start, end_time: end, hours: hrs }] : [],
      equipment: [],
      client_email: site?.client_email || '',
      employee_signer_name: '',
      client_signer_name: '',
      employee_signature: '',
      client_signature: '',
      client_report_no_hours: false
    });
    convertingJobRef.current = job.id;
    setEditingReport(null);
    setIsDialogOpen(true);
    if (!site && job.worksite_name) toast.warning(`Cantiere "${job.worksite_name}" non trovato: selezionalo manualmente`);
    if (!emp) toast.warning(`Dipendente matricola ${job.matricola} non trovato in RLS: aggiungilo manualmente`);
  };

  const handleSendToHr = async (reportId) => {
    setSendingToHr(true);
    try {
      const res = await axios.post(`${API}/reports/${reportId}/send-to-hr`);
      if (res.data.success) {
        toast.success(`${res.data.count} consuntivi inviati al portale HR!`);
      } else {
        const firstErr = res.data.items?.find(i => i.status === 'error')?.error || res.data.error;
        toast.error(`Invio HR con errori: ${firstErr || 'verifica il dettaglio'}`);
      }
      const updated = await axios.get(`${API}/reports/${reportId}`);
      setSelectedReportDetail(updated.data);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio al portale HR');
    } finally {
      setSendingToHr(false);
    }
  };

  const openReportDetail = (report) => {
    setSelectedReportDetail(report);
    setDiagnosisData(null);
    setShowDiagnosis(false);
    setReminderHistory(null);
    if (isAdminOrGestore()) {
      axios.get(`${API}/reports/${report.id}/reminders`)
        .then((r) => setReminderHistory(r.data))
        .catch(() => {});
    }
    setIsDetailDialogOpen(true);
  };

  // Diagnostica rapporto (solo admin)
  const handleDiagnoseReport = async (reportId) => {
    setLoadingDiagnosis(true);
    try {
      const response = await axios.get(`${API}/admin/diagnose-report/${reportId}`);
      setDiagnosisData(response.data);
      setShowDiagnosis(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore diagnostica');
    } finally {
      setLoadingDiagnosis(false);
    }
  };

  // Riprova invio email con diagnostica dettagliata
  const handleRetryEmail = async (reportId) => {
    try {
      const response = await axios.post(`${API}/admin/retry-email/${reportId}`);
      if (response.data.success) {
        toast.success(response.data.message);
        // Refresh diagnosis
        handleDiagnoseReport(reportId);
        fetchData();
      } else {
        toast.error(`Errore: ${response.data.error}`);
        // Refresh diagnosis to show new error
        handleDiagnoseReport(reportId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio');
    }
  };

  const openEmailDialog = (report, noHours = null) => {
    if (!report.employee_signature) { toast.error('Manca firma dipendente'); return; }
    setSelectedReportForEmail(report);
    setEmailNoHours(noHours);
    const site = sites.find(s => s.id === report.site_id);
    setEmailFormData({ client_email: report.client_email || site?.client_email || '', client_signature: report.client_signature || '' });
    setIsEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailFormData.client_email) { toast.error('Email obbligatoria'); return; }
    if (!emailFormData.client_signature) { toast.error('Firma obbligatoria'); return; }
    setSendingEmail(true);
    try {
      const payload = { report_id: selectedReportForEmail.id, ...emailFormData };
      if (emailNoHours !== null) payload.no_hours = emailNoHours;
      const response = await axios.post(`${API}/reports/${selectedReportForEmail.id}/send-email`, payload);
      toast.success(response.data.message);
      setIsEmailDialogOpen(false); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Errore'); }
    finally { setSendingEmail(false); }
  };

  const openApprovalDialog = (report) => {
    setSelectedReportForApproval(report);
    setApprovalLink('');
    setIsApprovalDialogOpen(true);
  };

  const handleSendApprovalEmail = async () => {
    if (!selectedReportForApproval) return;
    setSendingApproval(true);
    try {
      const response = await axios.post(`${API}/reports/${selectedReportForApproval.id}/send-approval-email`);
      setApprovalLink(response.data.approval_url);
      toast.success('Email di approvazione inviata!');
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Errore nell\'invio'); 
    }
    finally { setSendingApproval(false); }
  };

  const handleGenerateApprovalLink = async () => {
    if (!selectedReportForApproval) return;
    setSendingApproval(true);
    try {
      const response = await axios.post(`${API}/reports/${selectedReportForApproval.id}/generate-approval-link`);
      setApprovalLink(response.data.approval_url);
      toast.success('Link generato!');
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Errore'); 
    }
    finally { setSendingApproval(false); }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(approvalLink);
    toast.success('Link copiato!');
  };

  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exportSortBy, setExportSortBy] = useState('date');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState('fatture');
  const [exportFilters, setExportFilters] = useState({
    client_name: '',
    site_id: '',
    date_from: '',
    date_to: '',
    sort_by: 'date',
    date_filter_by: 'intervention'
  });

  const uniqueClients = useMemo(() => {
    const clients = [...new Set(sites.filter(s => s.client_name).map(s => s.client_name))];
    return clients.sort();
  }, [sites]);

  // Cantieri selezionabili nei form: solo attivi (+ quello già associato al rapporto in modifica)
  const activeSites = useMemo(() => sites.filter(s => s.active !== false), [sites]);
  const formSites = useMemo(
    () => sites.filter(s => s.active !== false || s.id === formData.site_id || s.id === editingReport?.site_id),
    [sites, formData.site_id, editingReport]
  );

  const handleExportPDF = async (exportTypeOverride = null, sortByOverride = null) => {
    try {
      const type = exportTypeOverride || exportType;
      const params = new URLSearchParams();
      
      // Usa i filtri del dialog se presenti, altrimenti i filtri della pagina
      if (exportFilters.date_from && exportFilters.date_to) {
        params.append('date_from', exportFilters.date_from);
        params.append('date_to', exportFilters.date_to);
      } else {
        if (filterMonth) params.append('month', filterMonth);
        if (filterYear) params.append('year', filterYear);
      }
      if (exportFilters.client_name) params.append('client_name', exportFilters.client_name);
      if (exportFilters.site_id) params.append('site_id', exportFilters.site_id);
      if (filterEmployee !== 'all') params.append('employee_id', filterEmployee);
      
      const sortParam = sortByOverride || exportFilters.sort_by || exportSortBy;
      if (sortParam) params.append('sort_by', sortParam);
      
      // Passa il criterio di filtro data (intervento o rapporto)
      if (exportFilters.date_filter_by) params.append('date_filter_by', exportFilters.date_filter_by);
      
      let endpoint = `${API}/reports/export/pdf`;
      let filename = `rapporti.pdf`;
      
      if (type === 'fatture') {
        endpoint = `${API}/reports/export/fatture`;
        filename = `report_fatture.pdf`;
      } else if (type === 'paghe') {
        endpoint = `${API}/reports/export/paghe`;
        filename = `report_paghe.pdf`;
      }
      
      const response = await axios.get(`${endpoint}?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success('PDF scaricato!');
      setExportDialogOpen(false);
      setExportMenuOpen(false);
    } catch { toast.error('Nessun rapporto trovato con i filtri selezionati'); }
  };

  // Handle sort toggle
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Get sort icon for column header
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-[#1B6B93]" />
      : <ArrowDown className="w-3 h-3 ml-1 text-[#1B6B93]" />;
  };

  // Extract report number for sorting (e.g., "15/2025" -> 15)
  const extractReportNumber = (reportNumber) => {
    if (!reportNumber) return 0;
    const parts = reportNumber.split('/');
    return parseInt(parts[0]) || 0;
  };

  const filteredReports = reports
    .filter(r => {
      if (!r) return false; // Protezione contro rapporti null
      if (filterStatus !== 'all' && r.status !== filterStatus) return false;
      if (filterEmployee !== 'all' && r.employee_id !== filterEmployee) return false;
      if (filterYear && r.date && !r.date.startsWith(filterYear)) return false;
      if (filterMonth && r.date && !r.date.startsWith(`${filterYear}-${filterMonth.padStart(2, '0')}`)) return false;
      if (filterSite !== 'all' && r.site_id !== filterSite) return false;
      if (filterClient !== 'all') {
        const site = sites.find(s => s.id === r.site_id);
        if (!site || site.client_name !== filterClient) return false;
      }
      return true;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'report_number':
          comparison = extractReportNumber(a.report_number) - extractReportNumber(b.report_number);
          break;
        case 'created_at':
          comparison = new Date(a.created_at) - new Date(b.created_at);
          break;
        case 'date':
          comparison = new Date(a.date) - new Date(b.date);
          break;
        case 'site_name':
          comparison = (a.site_name || '').localeCompare(b.site_name || '');
          break;
        case 'ticket':
          comparison = (a.ticket || '').localeCompare(b.ticket || '', 'it', { numeric: true });
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  const getStatusBadge = (status) => {
    const styles = { 
      pending: 'status-badge status-pending', 
      approved: 'status-badge status-approved', 
      rejected: 'status-badge status-rejected',
      draft: 'bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium'
    };
    const labels = { pending: 'In Attesa', approved: 'Approvato', rejected: 'Rifiutato', draft: '📝 Bozza' };
    return <span className={styles[status] || styles.pending}>{labels[status] || status}</span>;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B6B93]"></div></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Rapporti di Lavoro Straordinario</h1>
            <p className="text-slate-500 mt-1">{filteredReports.length} rapporti</p>
          </div>
          
          {/* Desktop buttons */}
          <div className="hidden md:flex gap-3">
            {/* Export Button - apre il dialog filtri */}
            <Button 
              onClick={() => { setExportDialogOpen(true); setExportFilters({ client_name: '', site_id: '', date_from: '', date_to: '', sort_by: 'date', date_filter_by: 'intervention' }); }} 
              variant="outline" 
              className="btn-outline"
            >
              <Download className="w-4 h-4 mr-2" />Esporta Report
            </Button>
            
            {/* Multi-Day Report Button */}
            <Dialog open={isMultiDayDialogOpen} onOpenChange={(open) => { setIsMultiDayDialogOpen(open); if (!open) setEditingReport(null); }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-green-500 text-green-600 hover:bg-green-50">
                  <CalendarDays className="w-4 h-4 mr-2" />+ Nuovo Rapporto Multigiorno
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto bg-white">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold text-[#1B6B93]">{editingReport?.is_multi_day ? 'Modifica Rapporto Multi-Giorno' : 'Nuovo Rapporto Multi-Giorno'}</DialogTitle>
                </DialogHeader>
                <MultiDayReportForm
                  sites={formSites}
                  employees={employees}
                  editingReport={editingReport?.is_multi_day ? editingReport : null}
                  duplicateData={duplicateReportData}
                  pendingTickets={pendingTickets}
                  activityTypes={activityTypes}
                  onSuccess={() => { setIsMultiDayDialogOpen(false); setEditingReport(null); setDuplicateReportData(null); fetchData(); }}
                  onCancel={() => { setIsMultiDayDialogOpen(false); setEditingReport(null); setDuplicateReportData(null); }}
                />
              </DialogContent>
            </Dialog>
          <Dialog open={isDialogOpen && !editingReport?.is_multi_day} onOpenChange={(open) => { if (!isUploadingPhotos) { setIsDialogOpen(open); if (!open) { setEditingReport(null); resetForm(); setShowNewSiteForm(false); } } }}>
            <DialogTrigger asChild><Button className="btn-primary" data-testid="new-report-btn"><Plus className="w-4 h-4 mr-2" />Nuovo Rapporto</Button></DialogTrigger>
            <DialogContent data-testid="report-dialog-content" className="max-w-3xl max-h-[95vh] overflow-y-auto bg-white pb-20 md:pb-6">
              {/* Overlay durante upload foto - FIXED position per essere sempre visibile */}
              {isUploadingPhotos && (
                <div className="fixed inset-0 bg-white/95 z-[100] flex flex-col items-center justify-center">
                  <div className="text-center space-y-4 p-8">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#1B6B93] border-t-transparent mx-auto"></div>
                    <h3 className="text-xl font-semibold text-[#1B6B93]">
                      {uploadProgress.status === 'compressing' ? 'Ottimizzazione foto...' : 'Caricamento foto in corso...'}
                    </h3>
                    <p className="text-slate-600">Foto {uploadProgress.current} di {uploadProgress.total}</p>
                    <div className="w-64 bg-slate-200 rounded-full h-3 mx-auto">
                      <div 
                        className="bg-[#1B6B93] h-3 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress.total > 0 ? (uploadProgress.current / uploadProgress.total) * 100 : 0}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-slate-500">Non chiudere questa finestra</p>
                  </div>
                </div>
              )}
              <DialogHeader><DialogTitle className="text-xl font-bold text-[#1B6B93]">{editingReport ? 'Modifica Rapporto' : 'Nuovo Rapporto di Lavoro Straordinario'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Row 1: Data e Cantiere - stack su mobile */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Data</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required className="input-field bg-white" /></div>
                  <div className="space-y-2"><Label>Cantiere</Label>
                    <div className="flex gap-2">
                      <Select value={formData.site_id} onValueChange={handleSiteChange}>
                        <SelectTrigger className="input-field bg-white flex-1"><SelectValue placeholder="Seleziona cantiere" /></SelectTrigger>
                        <SelectContent>{formSites.map(s => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
                      </Select>
                      <Button type="button" variant="outline" size="icon" onClick={() => setShowNewSiteForm(!showNewSiteForm)} className="shrink-0 h-10 w-10" title="Aggiungi nuovo cantiere">
                        <Building2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {/* Mini-form per nuovo cantiere */}
                    {showNewSiteForm && (
                      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                        <p className="text-sm font-medium text-blue-800">Nuovo Cantiere/Cliente</p>
                        <Input value={newSiteData.name} onChange={(e) => setNewSiteData({ ...newSiteData, name: e.target.value })} placeholder="Nome cantiere *" className="bg-white h-9" />
                        <Input value={newSiteData.address} onChange={(e) => setNewSiteData({ ...newSiteData, address: e.target.value })} placeholder="Indirizzo" className="bg-white h-9" />
                        <Input value={newSiteData.client_name} onChange={(e) => setNewSiteData({ ...newSiteData, client_name: e.target.value })} placeholder="Nome cliente" className="bg-white h-9" />
                        <Input type="email" value={newSiteData.client_email} onChange={(e) => setNewSiteData({ ...newSiteData, client_email: e.target.value })} placeholder="Email cliente" className="bg-white h-9" />
                        <div className="flex gap-2">
                          <Button type="button" size="sm" onClick={handleCreateSite} disabled={creatingSite} className="bg-blue-600 hover:bg-blue-700">{creatingSite ? 'Creazione...' : 'Crea Cantiere'}</Button>
                          <Button type="button" size="sm" variant="outline" onClick={() => setShowNewSiteForm(false)}>Annulla</Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Row 2: Default times - responsive */}
                <div className="grid grid-cols-3 gap-2 md:gap-4">
                  <div className="space-y-2"><Label className="text-xs md:text-sm">Inizio</Label><Input type="time" value={formData.start_time} onChange={(e) => handleTimeChange('start_time', e.target.value)} required className="input-field bg-white text-sm" /></div>
                  <div className="space-y-2"><Label className="text-xs md:text-sm">Fine</Label><Input type="time" value={formData.end_time} onChange={(e) => handleTimeChange('end_time', e.target.value)} required className="input-field bg-white text-sm" /></div>
                  <div className="space-y-2"><Label className="text-[#1B6B93] font-bold text-xs md:text-sm">ORE</Label><Input type="text" value={formatHours(formData.total_hours)} readOnly className="input-field font-mono font-bold text-base md:text-lg bg-[#A8D8EA]" /></div>
                </div>
                
                {/* Activity */}
                <div className="space-y-2"><Label>Tipo di Attività</Label>
                  <Select value={formData.activity_type} onValueChange={(v) => setFormData({ ...formData, activity_type: v })}>
                    <SelectTrigger className="input-field bg-white"><SelectValue placeholder="Seleziona attività" /></SelectTrigger>
                    <SelectContent>{activityTypes.map(a => (<SelectItem key={a} value={a}>{a}</SelectItem>))}</SelectContent>
                  </Select>
                </div>

                {/* Individual times toggle */}
                <label 
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-colors cursor-pointer select-none ${useIndividualTimes ? 'bg-emerald-50 border-emerald-400' : 'bg-slate-50 border-transparent'}`}
                >
                  <input 
                    type="checkbox"
                    checked={useIndividualTimes}
                    onChange={(e) => setUseIndividualTimes(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-9 h-5 rounded-full transition-colors ${useIndividualTimes ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                    <div className={`w-4 h-4 mt-0.5 rounded-full bg-white shadow-sm transition-transform ${useIndividualTimes ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'}`} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className={`w-4 h-4 ${useIndividualTimes ? 'text-emerald-600' : 'text-[#1B6B93]'}`} />
                    <span className={`font-medium ${useIndividualTimes ? 'text-emerald-700' : ''}`}>Orari individuali per dipendente</span>
                  </div>
                  {useIndividualTimes && <span className="text-xs text-emerald-600 font-medium ml-auto">ATTIVO</span>}
                </label>

                {/* Employees */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">Dipendenti <span className="text-xs bg-[#1B6B93] text-white px-2 py-0.5 rounded-full">{formData.employees.length}</span></Label>
                  <Select value="none" onValueChange={handleAddEmployee}>
                    <SelectTrigger className="input-field bg-white"><SelectValue placeholder="Aggiungi dipendente..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" disabled>Seleziona dipendente</SelectItem>
                      {employees.filter(e => !formData.employees.some(fe => fe.employee_id === e.id)).map(e => (<SelectItem key={e.id} value={e.id}>{e.name} ({e.matricola})</SelectItem>))}
                    </SelectContent>
                  </Select>
                  {formData.employees.length > 0 && (
                    <div className="space-y-2 mt-2 p-3 bg-slate-50 rounded-lg">
                      {formData.employees.map(emp => (
                        <div key={emp.employee_id} className="flex flex-col sm:flex-row items-start sm:items-center gap-2 p-2 bg-white rounded border">
                          <div className="flex items-center gap-2 w-full sm:w-auto">
                            <span className="font-medium text-sm">{emp.employee_name}</span>
                            <span className="text-xs text-slate-500">({emp.employee_matricola})</span>
                            <button type="button" onClick={() => handleRemoveEmployee(emp.employee_id)} className="text-red-500 hover:bg-red-100 p-1 rounded ml-auto sm:hidden"><X className="w-4 h-4" /></button>
                          </div>
                          {useIndividualTimes ? (
                            <div className="flex items-center gap-2 w-full sm:w-auto">
                              <Input type="time" value={emp.start_time || formData.start_time} onChange={(e) => handleEmployeeTimeChange(emp.employee_id, 'start_time', e.target.value)} className="w-full sm:w-24 h-10 sm:h-8 text-sm input-field bg-white" />
                              <span className="text-slate-400">-</span>
                              <Input type="time" value={emp.end_time || formData.end_time} onChange={(e) => handleEmployeeTimeChange(emp.employee_id, 'end_time', e.target.value)} className="w-full sm:w-24 h-10 sm:h-8 text-sm input-field bg-white" />
                              <span className="text-sm font-mono bg-[#A8D8EA] px-2 py-1 rounded whitespace-nowrap">{formatHours(emp.hours)}h</span>
                            </div>
                          ) : (
                            <span className="text-sm text-slate-500 sm:ml-auto">{formatHours(formData.hours_per_employee)}h</span>
                          )}
                          <button type="button" onClick={() => handleRemoveEmployee(emp.employee_id)} className="hidden sm:block text-red-500 hover:bg-red-100 p-1 rounded"><X className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-slate-500">Ore totali: <strong>{formatHours(formData.total_hours)}h</strong></p>
                </div>

                {/* Equipment */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Wrench className="w-4 h-4" /> Attrezzature <span className="text-xs bg-[#4BA3C7] text-white px-2 py-0.5 rounded-full">{formData.equipment.length}</span></Label>
                  <Select value="none" onValueChange={handleAddEquipment}>
                    <SelectTrigger className="input-field bg-white"><SelectValue placeholder="Aggiungi attrezzatura..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" disabled>Seleziona attrezzatura</SelectItem>
                      {equipmentOptions.map(eq => (<SelectItem key={eq} value={eq}>{eq}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  {formData.equipment.length > 0 && (
                    <div className="space-y-3 mt-2 p-3 bg-slate-50 rounded-lg">
                      {formData.equipment.map((eq, eqIndex) => (
                        <div key={`${eq.equipment_name}-${eqIndex}`} className="space-y-2 pb-2 border-b border-slate-200 last:border-0">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{eq.equipment_name}</span>
                            <button type="button" onClick={() => handleRemoveEquipment(eqIndex)} className="text-red-500 hover:bg-red-100 p-1 rounded"><X className="w-4 h-4" /></button>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-slate-500 w-10">Dalle:</span>
                            <input 
                              type="time"
                              value={eq.start_time || formData.start_time || ''}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                const idx = eqIndex;
                                setFormData(prev => ({
                                  ...prev,
                                  equipment: prev.equipment.map((item, i) => 
                                    i === idx ? { ...item, start_time: newValue } : item
                                  )
                                }));
                              }}
                              className="w-24 h-8 text-sm px-2 border border-gray-300 rounded bg-white"
                            />
                            <span className="text-xs text-slate-500 w-10">Alle:</span>
                            <input 
                              type="time"
                              value={eq.end_time || formData.end_time || ''}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                const idx = eqIndex;
                                setFormData(prev => ({
                                  ...prev,
                                  equipment: prev.equipment.map((item, i) => 
                                    i === idx ? { ...item, end_time: newValue } : item
                                  )
                                }));
                              }}
                              className="w-24 h-8 text-sm px-2 border border-gray-300 rounded bg-white"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Reference Number */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Ticket className="w-4 h-4" /> Ns./Vs. Rif. (opzionale)</Label>
                  <Input type="text" value={formData.ticket} onChange={(e) => setFormData({ ...formData, ticket: e.target.value })} placeholder="Riferimento interno/esterno" className="input-field bg-white" />
                </div>

                {/* Ticket da App Ticket */}
                {!editingReport && (
                  pendingTickets.length > 0 ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2 text-blue-700 font-medium">
                        <Ticket className="w-5 h-5" />
                        <span>Ticket Clienti ({pendingTickets.length} in attesa)</span>
                      </div>
                      <p className="text-sm text-blue-600">
                        Seleziona un ticket per collegare questo rapporto all'intervento richiesto.
                      </p>
                      <Select
                        value={selectedTicket?.ticket_id || "none"}
                        onValueChange={(value) => {
                          if (value === "none") {
                            setSelectedTicket(null);
                            return;
                          }
                          const ticket = pendingTickets.find(t => t.ticket_id === value);
                          if (ticket) {
                            setSelectedTicket(ticket);
                            // Pre-popola i campi dal ticket
                            const site = activeSites.find(s => 
                              s.name.toLowerCase().includes(ticket.cantiere_name?.toLowerCase()) ||
                              ticket.cantiere_name?.toLowerCase().includes(s.name.toLowerCase())
                            );
                            // Recupera l'email primaria dal cantiere trovato
                            const siteEmail = site?.emails?.find(e => e.is_primary)?.address || site?.emails?.[0]?.address || '';
                            setFormData(prev => ({
                              ...prev,
                              ticket: ticket.ticket_number,
                              ticket_id: ticket.ticket_id,
                              site_id: site?.id || prev.site_id,
                              client_email: siteEmail || prev.client_email,
                              notes: ticket.description ? `[${ticket.ticket_number}] ${ticket.description}` : prev.notes,
                              activity_type: ticket.subject?.toLowerCase().includes('pulizia') ? 'Pulizia straordinaria' : 
                                            ticket.subject?.toLowerCase().includes('sanific') ? 'Sanificazione' : prev.activity_type
                            }));
                            toast.success(`Ticket ${ticket.ticket_number} selezionato`);
                          }
                        }}
                      >
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Seleziona un ticket..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- Nessun ticket --</SelectItem>
                          {pendingTickets.map(ticket => (
                            <SelectItem key={ticket.ticket_id} value={ticket.ticket_id}>
                              <div className="flex flex-col">
                                <span className="font-medium">{ticket.ticket_number} - {ticket.company_name}</span>
                                <span className="text-xs text-slate-500">{ticket.cantiere_name} • {ticket.subject}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Dettagli ticket selezionato */}
                      {selectedTicket && (
                        <div className="bg-white rounded-md p-3 text-sm space-y-1 border border-blue-100">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Cliente:</span>
                            <span className="font-medium">{selectedTicket.company_name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Cantiere:</span>
                            <span className="font-medium">{selectedTicket.cantiere_name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Priorità:</span>
                            <span className={`font-medium ${selectedTicket.priority === 'alta' ? 'text-red-600' : selectedTicket.priority === 'media' ? 'text-amber-600' : 'text-green-600'}`}>
                              {selectedTicket.priority || 'normale'}
                            </span>
                          </div>
                          {selectedTicket.ore_previste && (
                            <div className="flex justify-between">
                              <span className="text-slate-500">Ore previste:</span>
                              <span className="font-medium">{selectedTicket.ore_previste}h</span>
                            </div>
                          )}
                          {selectedTicket.description && (
                            <div className="pt-2 border-t">
                              <span className="text-slate-500 block mb-1">Descrizione:</span>
                              <p className="text-slate-700">{selectedTicket.description}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center gap-2 text-slate-500">
                      <Ticket className="w-4 h-4" />
                      <span className="text-sm">Nessun ticket clienti in attesa</span>
                    </div>
                  )
                )}

                {/* Email */}
                <div className="space-y-2"><Label>Email Cliente *</Label><Input type="email" value={formData.client_email} onChange={(e) => setFormData({ ...formData, client_email: e.target.value })} placeholder="cliente@email.com" required className="input-field bg-white" /><p className="text-xs text-slate-500">Inviato anche a straordinari@splendorsud.com</p></div>
                
                {/* Notes */}
                <div className="space-y-2"><Label>Note (opzionale)</Label><Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Note aggiuntive..." className="input-field bg-white min-h-[60px]" /></div>

                {/* Photo Upload - sempre visibile */}
                <PhotoUpload
                  reportId={editingReport?.id}
                  photos={editingReport?.photos || []}
                  onPhotosChange={(newPhotos) => {
                    if (editingReport) {
                      setEditingReport({ ...editingReport, photos: newPhotos });
                    }
                  }}
                  pendingPhotos={pendingPhotos}
                  onPendingPhotosChange={setPendingPhotos}
                  disabled={false}
                />

                {/* Allegati PDF - solo admin */}
                {isAdmin() && (
                  <div className="space-y-3">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Allegati PDF
                    </Label>
                    {/* Lista allegati esistenti */}
                    {(editingReport?.attachments || []).map((att) => (
                      <div key={att.id} className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                        <a href={`${API}/attachments/${att.filename}`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-700 hover:underline truncate mr-2">
                          {att.original_name}
                        </a>
                        <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500 hover:bg-red-100 shrink-0" onClick={async () => {
                          try {
                            await axios.delete(`${API}/reports/${editingReport.id}/attachments/${att.id}`);
                            setEditingReport({ ...editingReport, attachments: editingReport.attachments.filter(a => a.id !== att.id) });
                            toast.success('Allegato eliminato');
                          } catch { toast.error('Errore eliminazione'); }
                        }}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    ))}
                    {/* Upload nuovo allegato */}
                    {editingReport?.id && (
                      <label className="flex items-center gap-2 cursor-pointer text-sm text-blue-600 hover:text-blue-800">
                        <Plus className="w-4 h-4" /> Allega PDF
                        <input type="file" accept=".pdf,application/pdf" className="hidden" onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const fd = new FormData();
                          fd.append('file', file);
                          try {
                            const res = await axios.post(`${API}/reports/${editingReport.id}/attachments`, fd);
                            setEditingReport({ ...editingReport, attachments: [...(editingReport.attachments || []), res.data.attachment] });
                            toast.success('PDF allegato!');
                          } catch (err) { toast.error(err.response?.data?.detail || 'Errore upload'); }
                          e.target.value = '';
                        }} />
                      </label>
                    )}
                    {!editingReport?.id && <p className="text-xs text-slate-500">Salva il rapporto per poter allegare PDF</p>}
                  </div>
                )}

                {/* Toggle PDF senza ore per cliente */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="clientReportNoHoursSingle"
                      checked={formData.client_report_no_hours}
                      onChange={(e) => setFormData({ ...formData, client_report_no_hours: e.target.checked })}
                      className="w-5 h-5 text-amber-600 rounded border-amber-300 focus:ring-amber-500"
                    />
                    <label htmlFor="clientReportNoHoursSingle" className="text-sm font-medium text-amber-800 cursor-pointer flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Genera PDF per Cliente (senza indicazione ore)
                    </label>
                  </div>
                  <p className="text-xs text-amber-600 mt-2 ml-8">
                    Il PDF mostrerà solo l'attività svolta, senza dettagli su ore e orari di lavoro
                  </p>
                </div>

                {/* Signatures */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Nome e Cognome Dipendente Firmatario {!isAdminOrGestore() && '*'}</Label>
                      <Input value={formData.employee_signer_name} onChange={(e) => setFormData({ ...formData, employee_signer_name: e.target.value })} placeholder="Mario Rossi" required={!isAdminOrGestore()} className="input-field bg-white" />
                    </div>
                    <SignaturePad label={`Firma Dipendente ${!isAdminOrGestore() ? '*' : '(opzionale)'}`} savedSignature={formData.employee_signature} onSave={(sig) => setFormData({ ...formData, employee_signature: sig })} onClear={() => setFormData({ ...formData, employee_signature: '' })} />
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Nome e Cognome Cliente Firmatario {!isAdminOrGestore() && '*'}</Label>
                      <Input value={formData.client_signer_name} onChange={(e) => setFormData({ ...formData, client_signer_name: e.target.value })} placeholder="Giuseppe Verdi" required={!isAdminOrGestore()} className="input-field bg-white" />
                    </div>
                    <SignaturePad label={`Firma Cliente ${!isAdminOrGestore() ? '*' : '(opzionale)'}`} savedSignature={formData.client_signature} onSave={(sig) => setFormData({ ...formData, client_signature: sig })} onClear={() => setFormData({ ...formData, client_signature: '' })} />
                  </div>
                </div>
                
                {/* Checkbox per inviare richiesta approvazione - solo per admin/gestore senza firma cliente */}
                {isAdminOrGestore() && !formData.client_signature && !skipEmailSend && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-4">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="sendApprovalOnCreate"
                        checked={sendApprovalOnCreate}
                        onChange={(e) => setSendApprovalOnCreate(e.target.checked)}
                        className="w-5 h-5 text-purple-600 rounded border-purple-300 focus:ring-purple-500"
                      />
                      <label htmlFor="sendApprovalOnCreate" className="text-sm font-medium text-purple-800 cursor-pointer">
                        📧 Invia richiesta di approvazione al cliente
                      </label>
                    </div>
                    <p className="text-xs text-purple-600 mt-2 ml-8">
                      Il cliente riceverà un'email con un link per approvare o rifiutare il rapporto online
                    </p>
                  </div>
                )}
                
                {/* Checkbox per saltare invio email - SOLO ADMIN */}
                {user?.role === 'admin' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="skipEmailSend"
                        checked={skipEmailSend}
                        onChange={(e) => {
                          setSkipEmailSend(e.target.checked);
                          if (e.target.checked) setSendApprovalOnCreate(false);
                        }}
                        className="w-5 h-5 text-amber-600 rounded border-amber-300 focus:ring-amber-500"
                      />
                      <label htmlFor="skipEmailSend" className="text-sm font-medium text-amber-800 cursor-pointer">
                        📋 Rapporto già consegnato (non inviare email)
                      </label>
                    </div>
                    <p className="text-xs text-amber-600 mt-2 ml-8">
                      Usa questa opzione per caricare rapporti cartacei già consegnati al cliente
                    </p>
                  </div>
                )}
                
                {/* Submit Buttons - pb-16 to avoid "Made with Emergent" watermark on mobile */}
                <div className="flex flex-wrap justify-end gap-3 pt-4 pb-16 md:pb-4 border-t">
                  <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); setCorrectingReport(false); }}>Annulla</Button>
                  {/* Salva Bozza - nuovi rapporti O modifica rapporti resettati */}
                  {!editingReport && (isAdminOrGestore() || user?.can_create_drafts) && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="border-amber-500 text-amber-600 hover:bg-amber-50"
                      onClick={(e) => handleSubmit(e, true)}
                      disabled={isSubmitting}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Salva Bozza
                    </Button>
                  )}
                  <Button type="submit" className="btn-primary" disabled={isSubmitting || (!isAdminOrGestore() && !correctingReport && (!formData.employee_signature || !formData.client_signature))}>
                    {isSubmitting ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>{correctingReport ? 'Correzione...' : editingReport ? 'Aggiornamento...' : 'Creazione...'}</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" />
                      {correctingReport ? 'Salva Correzioni e Reinvia' : editingReport ? 'Aggiorna' : (sendApprovalOnCreate && !formData.client_signature ? 'Crea e Invia Approvazione' : 'Crea Rapporto')}</>
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        
        {/* Mobile Action Buttons */}
        <div className="md:hidden grid grid-cols-2 gap-3">
          <Button 
            className="btn-primary w-full h-14 text-sm" 
            onClick={() => setIsDialogOpen(true)}
          >
            <Plus className="w-5 h-5 mr-2" />
            Nuovo Rapporto
          </Button>
          
          <Button 
            variant="outline" 
            className="border-green-500 text-green-600 hover:bg-green-50 w-full h-14 text-sm"
            onClick={() => setIsMultiDayDialogOpen(true)}
          >
            <CalendarDays className="w-5 h-5 mr-2" />
            Multigiorno
          </Button>
        </div>
      </div>
    </div>

      {/* Email Dialog */}
      <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#1B6B93] flex items-center gap-2"><Mail className="w-5 h-5" /> Invia Rapporto</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2"><Label>Email Cliente *</Label><Input type="email" value={emailFormData.client_email} onChange={(e) => setEmailFormData({ ...emailFormData, client_email: e.target.value })} placeholder="cliente@email.com" className="input-field bg-white" required /><p className="text-xs text-slate-500">Inviato anche a straordinari@splendorsud.com</p></div>
            <SignaturePad label="Firma Cliente" savedSignature={emailFormData.client_signature} onSave={(sig) => setEmailFormData({ ...emailFormData, client_signature: sig })} onClear={() => setEmailFormData({ ...emailFormData, client_signature: '' })} />
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEmailDialogOpen(false)}>Annulla</Button>
              <Button onClick={handleSendEmail} className="btn-primary" disabled={sendingEmail || !emailFormData.client_signature}>{sendingEmail ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Invio...</> : <><Send className="w-4 h-4 mr-2" />Invia</>}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <Card className="shadow-sm"><CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Filter className="w-5 h-5 text-slate-400" />
          <Select value={filterStatus} onValueChange={setFilterStatus}><SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="draft">Bozze</SelectItem><SelectItem value="pending">In Attesa</SelectItem><SelectItem value="approved">Approvati</SelectItem><SelectItem value="rejected">Rifiutati</SelectItem></SelectContent></Select>
          {isAdminOrGestore() && (<Select value={filterEmployee} onValueChange={setFilterEmployee}><SelectTrigger className="w-[150px]"><SelectValue placeholder="Dipendente" /></SelectTrigger><SelectContent><SelectItem value="all">Tutti i dip.</SelectItem>{employees.map(e => (<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}</SelectContent></Select>)}
          <Select value={filterClient} onValueChange={(v) => { setFilterClient(v); if (v !== 'all') setFilterSite('all'); }}><SelectTrigger className="w-[150px]"><SelectValue placeholder="Cliente" /></SelectTrigger><SelectContent><SelectItem value="all">Tutti i clienti</SelectItem>{[...new Set(sites.map(s => s.client_name).filter(Boolean))].sort().map(c => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent></Select>
          <Select value={filterSite} onValueChange={setFilterSite}><SelectTrigger className="w-[150px]"><SelectValue placeholder="Cantiere" /></SelectTrigger><SelectContent><SelectItem value="all">Tutti i cantieri</SelectItem>{sites.filter(s => filterClient === 'all' || s.client_name === filterClient).sort((a,b) => a.name.localeCompare(b.name)).map(s => (<SelectItem key={s.id} value={s.id}>{s.name}{s.active === false ? ' (archiviato)' : ''}</SelectItem>))}</SelectContent></Select>
          <Select value={filterMonth || "all"} onValueChange={(v) => setFilterMonth(v === "all" ? "" : v)}><SelectTrigger className="w-[120px]"><SelectValue placeholder="Mese" /></SelectTrigger><SelectContent><SelectItem value="all">Tutti i mesi</SelectItem>{[...Array(12)].map((_, i) => (<SelectItem key={i+1} value={String(i+1)}>{new Date(2000,i).toLocaleDateString('it-IT',{month:'long'})}</SelectItem>))}</SelectContent></Select>
          <Select value={filterYear} onValueChange={setFilterYear}><SelectTrigger className="w-[90px]"><SelectValue /></SelectTrigger><SelectContent>{[2024,2025,2026].map(y => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}</SelectContent></Select>
          
          {/* Sort indicator */}
          <div className="ml-auto flex items-center gap-2 text-sm text-slate-500">
            <ArrowUpDown className="w-4 h-4" />
            <span>Ordina per:</span>
            <Select value={sortField} onValueChange={(v) => setSortField(v)}>
              <SelectTrigger className="w-[130px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="report_number">N. Rapporto</SelectItem>
                <SelectItem value="created_at">Data Rapporto</SelectItem>
                <SelectItem value="date">Date Intervento</SelectItem>
                <SelectItem value="site_name">Cantiere</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 px-2"
              onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
            >
              {sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </CardContent></Card>

      {/* Pianificati da HR */}
      {isAdminOrGestore() && hrPlannedJobs.filter(j => !j.converted_report_id).length > 0 && (
        <Card className="shadow-sm border-l-4 border-l-indigo-500" data-testid="hr-planned-jobs-card">
          <CardContent className="p-4">
            <button
              type="button"
              className="w-full flex items-center justify-between"
              onClick={() => setShowPlannedJobs(!showPlannedJobs)}
              data-testid="hr-planned-jobs-toggle"
            >
              <div className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-indigo-600" />
                <span className="font-semibold text-slate-700">Pianificati da HR</span>
                <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-0.5 rounded-full">
                  {hrPlannedJobs.filter(j => !j.converted_report_id).length}
                </span>
              </div>
              <span className="text-sm text-indigo-600">{showPlannedJobs ? 'Nascondi' : 'Mostra'}</span>
            </button>
            {showPlannedJobs && (
              <div className="mt-4 space-y-2">
                {hrPlannedJobs.filter(j => !j.converted_report_id).map(job => (
                  <div key={job.id} className="flex flex-col md:flex-row md:items-center gap-2 bg-indigo-50/50 border border-indigo-100 rounded-lg p-3" data-testid={`hr-planned-job-${job.hr_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-semibold">{job.date_it || job.date}</span>
                        <span className="text-slate-600">{job.dipendente} ({job.matricola})</span>
                        {job.time_from && <span className="text-slate-500">{job.time_from} - {job.time_to}</span>}
                        <span className="font-medium text-indigo-700">h {job.hours_it || formatHours(job.hours)}</span>
                        {job.status && <span className="bg-white border text-xs px-2 py-0.5 rounded-full text-slate-600">{job.status}</span>}
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-1">
                        {job.worksite_name && <span className="font-medium">{job.worksite_name} — </span>}{job.description}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700 shrink-0"
                      onClick={() => createFromPlannedJob(job)}
                      data-testid={`create-from-planned-job-${job.hr_id}`}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Crea Rapporto
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="shadow-sm"><CardContent className="p-0">
        {filteredReports.length === 0 ? (
          <div className="text-center py-12"><FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">Nessun rapporto</p></div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto scrollbar-top">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th 
                      className="cursor-pointer hover:bg-slate-100 select-none whitespace-nowrap"
                      onClick={() => handleSort('report_number')}
                    >
                      <div className="flex items-center">N° RLS{getSortIcon('report_number')}</div>
                    </th>
                    <th 
                      className="cursor-pointer hover:bg-slate-100 select-none whitespace-nowrap"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center">Data Rap.{getSortIcon('created_at')}</div>
                    </th>
                    <th 
                      className="cursor-pointer hover:bg-slate-100 select-none whitespace-nowrap"
                      onClick={() => handleSort('date')}
                    >
                      <div className="flex items-center">Intervento{getSortIcon('date')}</div>
                    </th>
                    {isAdminOrGestore() && <th className="whitespace-nowrap">Creato da</th>}
                    <th 
                      className="cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort('site_name')}
                    >
                      <div className="flex items-center">Cantiere{getSortIcon('site_name')}</div>
                    </th>
                    <th 
                      className="cursor-pointer hover:bg-slate-100 select-none whitespace-nowrap"
                      onClick={() => handleSort('ticket')}
                      title="Ns./Vs. Riferimento"
                    >
                      <div className="flex items-center">Ns./Vs. Rif.{getSortIcon('ticket')}</div>
                    </th>
                    <th className="whitespace-nowrap text-center">Dip.</th>
                    <th className="whitespace-nowrap">Ore</th>
                    <th className="whitespace-nowrap">Stato</th>
                    <th className="whitespace-nowrap">Email</th>
                    <th className="whitespace-nowrap">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReports.map(report => (
                    <tr key={report.id}>
                      <td className="font-mono text-sm text-[#1B6B93] font-bold">
                        <button 
                          onClick={() => openReportDetail(report)}
                          className="hover:underline cursor-pointer flex items-center gap-1"
                          title="Visualizza dettaglio"
                        >
                          {report.status === 'draft' ? <span className="text-amber-600 font-bold">BOZZA</span> : (report.report_number || '-')}
                          {report.is_multi_day && <span className="ml-1 text-xs bg-green-100 text-green-600 px-1 rounded" title="Multi-giorno">📅</span>}
                        </button>
                      </td>
                      <td className="font-medium">
                        {formatCreatedAt(report.created_at)}
                      </td>
                      <td className="font-medium">
                        {report.is_multi_day && report.num_workdays > 1 ? (
                          <span title={report.date_range}>{report.date_range ? report.date_range.replace(/(\d{4})-(\d{2})-(\d{2})/g, (m, y, mo, d) => `${d}/${mo}/${y}`) : formatDateIT(report.date)}</span>
                        ) : formatDateIT(report.date)}
                      </td>
                      {isAdminOrGestore() && <td><p className="font-medium text-sm">{report.employee_name}</p><p className="text-xs text-slate-400">{report.employee_matricola}</p></td>}
                      <td className="text-sm">{report.site_name}</td>
                      <td className="text-sm" data-testid={`report-ticket-${report.id}`}>
                        {report.ticket ? (
                          <div className="flex items-center gap-1 max-w-[170px] text-slate-700" title={report.ticket}>
                            <Ticket className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                            <span className="truncate min-w-0">{report.ticket}</span>
                          </div>
                        ) : <span className="text-slate-300">-</span>}
                      </td>
                      <td className="text-center">
                        <span className="bg-[#1B6B93] text-white text-xs px-2 py-0.5 rounded-full">
                          {report.is_multi_day ? report.num_employees : (report.employees?.length || 0)}
                        </span>
                        {report.is_multi_day && <span className="text-xs text-slate-400 ml-1">({report.num_workdays}gg)</span>}
                      </td>
                      <td className="font-mono font-bold text-[#1B6B93]">
                        <div>h {formatHours(report.total_hours)}</div>
                        {(report.night_hours > 0 || report.semifestive_hours > 0 || report.festive_hours > 0) && (
                          <div className="text-xs font-normal flex gap-1 mt-1">
                            {report.night_hours > 0 && <span className="bg-indigo-100 text-indigo-600 px-1 rounded" title="Ore notturne">🌙{formatHours(report.night_hours)}</span>}
                            {report.semifestive_hours > 0 && <span className="bg-orange-100 text-orange-600 px-1 rounded" title="Ore semifestive">📅{formatHours(report.semifestive_hours)}</span>}
                            {report.festive_hours > 0 && <span className="bg-red-100 text-red-600 px-1 rounded" title="Ore festive">🎉{formatHours(report.festive_hours)}</span>}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          {getStatusBadge(report.status)}
                          {report.photos && report.photos.length > 0 && (
                            <button 
                              onClick={async () => {
                                try {
                                  const response = await axios.get(`${API}/reports/${report.id}/photo-gallery-url`);
                                  if (response.data.url) {
                                    window.open(response.data.url, '_blank');
                                  }
                                } catch (error) {
                                  toast.error('Errore apertura galleria foto');
                                }
                              }}
                              className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full hover:bg-blue-200 transition-colors cursor-pointer flex items-center gap-1" 
                              title={`Visualizza ${report.photos.length} foto`}
                            >
                              <Camera className="w-3 h-3" /> {report.photos.length}
                            </button>
                          )}
                        </div>
                      </td>
                      <td>{report.email_sent ? <span className="text-xs text-emerald-600 flex items-center gap-1"><Check className="w-3 h-3" />Inviato</span> : <span className="text-xs text-slate-400">-</span>}</td>
                      <td>
                        <div className="flex items-center gap-0 flex-nowrap">
                          {/* Per le bozze: pulsante Conferma */}
                          {report.status === 'draft' && (
                            <>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-100" onClick={() => handleConfirmDraft(report.id)} title="Conferma e numera"><Check className="w-3.5 h-3.5" /></Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEdit(report)} title="Modifica bozza"><Pencil className="w-3.5 h-3.5" /></Button>
                            </>
                          )}
                          {isAdminOrGestore() && 
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-100" title="Scarica PDF"><FileDown className="w-3.5 h-3.5" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start">
                                <DropdownMenuItem onClick={() => handleDownloadPDF(report.id, false)}>PDF con ore</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDownloadPDF(report.id, true)}>PDF senza ore</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          }
                          {isAdminOrGestore() && report.status !== 'draft' && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className={`h-7 w-7 p-0 ${report.email_sent ? "text-orange-500 hover:bg-orange-50" : "text-[#4BA3C7] hover:bg-[#4BA3C7]/10"}`}
                                  title={report.email_sent ? "Reinvia email" : "Invia email"}
                                >
                                  <Send className="w-3.5 h-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start">
                                <DropdownMenuItem onClick={() => openEmailDialog(report, false)}>Invia con ore</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openEmailDialog(report, true)}>Invia senza ore</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          {isAdminOrGestore() && !report.client_signature && report.employee_signature && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-purple-600 hover:bg-purple-100" onClick={() => openApprovalDialog(report)} title="Invia per approvazione"><Mail className="w-3.5 h-3.5" /></Button>
                          )}
                          {isAdmin() && report.status === 'pending' && <><Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-100" onClick={() => handleApprove(report.id)} title="Approva"><Check className="w-3.5 h-3.5" /></Button><Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-100" onClick={() => handleReject(report.id)} title="Rifiuta"><X className="w-3.5 h-3.5" /></Button></>}
                          {isAdmin() && report.status === 'pending' && <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEdit(report)} title="Modifica"><Pencil className="w-3.5 h-3.5" /></Button>}
                          {/* Duplica: solo admin e gestore */}
                          {isAdminOrGestore() && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-cyan-600 hover:bg-cyan-100" onClick={() => handleDuplicate(report)} title="Duplica rapporto"><Copy className="w-3.5 h-3.5" /></Button>
                          )}
                          {/* Correggi: admin o dipendente con permesso, solo rapporti approvati */}
                          {(isAdmin() || (user?.can_correct_reports && report.employee_id === user?.id)) && report.status === 'approved' && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-orange-600 hover:bg-orange-100" onClick={() => handleCorrectReport(report)} title="Correggi rapporto (rigenera PDF e reinvia email)"><Wrench className="w-3.5 h-3.5" /></Button>
                          )}
                          {/* Elimina: admin sempre, dipendente solo proprie bozze */}
                          {(isAdmin() || (user?.can_create_drafts && report.status === 'draft' && report.employee_id === user?.id)) && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-100" onClick={() => handleDelete(report.id)} title="Elimina"><Trash2 className="w-3.5 h-3.5" /></Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {filteredReports.map(report => (
                <div key={report.id} className="bg-white rounded-lg border p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <button 
                        onClick={() => openReportDetail(report)}
                        className="font-mono text-sm text-[#1B6B93] font-bold hover:underline"
                        title="Visualizza dettaglio"
                      >
                        {report.status === 'draft' ? <span className="text-amber-600 font-bold">BOZZA</span> : (report.report_number || '-')}
                      </button>
                      <span className="mx-2 text-slate-300">|</span>
                      <span className="font-medium">{formatDateIT(report.date)}</span>
                    </div>
                    {getStatusBadge(report.status)}
                  </div>
                  <p className="text-sm font-medium text-slate-700 mb-1">{report.site_name}</p>
                  {report.ticket && (
                    <p className="text-xs text-slate-600 mb-1 flex items-center gap-1" data-testid={`report-ticket-mobile-${report.id}`}>
                      <Ticket className="w-3 h-3 text-slate-400" /> Rif.: {report.ticket}
                    </p>
                  )}
                  {isAdminOrGestore() && <p className="text-xs text-slate-500 mb-2">Creato da: {report.employee_name}</p>}
                  <div className="flex items-center gap-4 text-sm mb-2">
                    <span><strong>h {formatHours(report.total_hours)}</strong></span>
                    <span className="text-slate-400">|</span>
                    <span>{report.employees?.length || 0} dip.</span>
                    {report.email_sent && <span className="text-emerald-600 text-xs flex items-center gap-1"><Check className="w-3 h-3" />Email inviata</span>}
                  </div>
                  {/* Show special hours if present */}
                  {(report.night_hours > 0 || report.semifestive_hours > 0 || report.festive_hours > 0) && (
                    <div className="flex flex-wrap items-center gap-2 text-xs mb-3">
                      <span className="text-slate-500">Di cui:</span>
                      {report.night_hours > 0 && <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">🌙 Notturne: h {formatHours(report.night_hours)}</span>}
                      {report.semifestive_hours > 0 && <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded">📅 Semifestive: h {formatHours(report.semifestive_hours)}</span>}
                      {report.festive_hours > 0 && <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded">🎉 Festive: h {formatHours(report.festive_hours)}</span>}
                    </div>
                  )}
                  <div className="flex items-center gap-2 border-t pt-3">
                    {/* Per le bozze: pulsante Conferma */}
                    {report.status === 'draft' && (
                      <>
                        <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => handleConfirmDraft(report.id)}><Check className="w-4 h-4 mr-1" />Conferma</Button>
                        <Button size="sm" variant="outline" onClick={() => handleEdit(report)}><Pencil className="w-4 h-4 mr-1" />Modifica</Button>
                      </>
                    )}
                    {isAdminOrGestore() && 
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="outline" className="text-blue-600"><FileDown className="w-4 h-4 mr-1" />PDF</Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => handleDownloadPDF(report.id, false)}>Con ore</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownloadPDF(report.id, true)}>Senza ore</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    }
                    {isAdminOrGestore() && report.status !== 'draft' && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="outline" className={report.email_sent ? "text-orange-500" : "text-[#4BA3C7]"}>
                            <Send className="w-4 h-4 mr-1" />{report.email_sent ? "Reinvia" : "Email"}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => openEmailDialog(report, false)}>Con ore</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEmailDialog(report, true)}>Senza ore</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    {isAdminOrGestore() && !report.client_signature && report.employee_signature && report.status !== 'draft' && (
                      <Button size="sm" variant="outline" className="text-purple-600" onClick={() => openApprovalDialog(report)}><Mail className="w-4 h-4 mr-1" />Approva</Button>
                    )}
                    {isAdmin() && report.status === 'pending' && <Button size="sm" variant="outline" onClick={() => handleEdit(report)}><Pencil className="w-4 h-4 mr-1" />Modifica</Button>}
                    {isAdmin() && report.status === 'pending' && <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => handleApprove(report.id)}><Check className="w-4 h-4" /></Button>}
                    {/* Elimina: admin sempre, dipendente solo proprie bozze */}
                    {(isAdmin() || (user?.can_create_drafts && report.status === 'draft' && report.employee_id === user?.id)) && (
                      <Button size="sm" variant="outline" className="text-red-600 ml-auto" onClick={() => handleDelete(report.id)}><Trash2 className="w-4 h-4" /></Button>
                    )}
                    {/* Correggi: admin o dipendente con permesso, solo approvati */}
                    {(isAdmin() || (user?.can_correct_reports && report.employee_id === user?.id)) && report.status === 'approved' && (
                      <Button size="sm" variant="outline" className="text-orange-600" onClick={() => handleCorrectReport(report)}><Wrench className="w-4 h-4 mr-1" />Correggi</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent></Card>

      {/* Approval Dialog */}
      <Dialog open={isApprovalDialogOpen} onOpenChange={setIsApprovalDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1B6B93]">Invia per Approvazione</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Invia un'email al cliente con un link per approvare o rifiutare questo rapporto online.
            </p>
            {selectedReportForApproval && (
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p><strong>Rapporto:</strong> RLS n° {selectedReportForApproval.report_number}</p>
                <p><strong>Data:</strong> {selectedReportForApproval.date}</p>
                <p><strong>Cantiere:</strong> {selectedReportForApproval.site_name}</p>
              </div>
            )}
            
            {approvalLink && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm font-medium text-green-800 mb-2">Link generato:</p>
                <div className="flex items-center gap-2">
                  <input 
                    type="text" 
                    value={approvalLink} 
                    readOnly 
                    className="flex-1 text-xs bg-white border rounded px-2 py-1"
                  />
                  <Button size="sm" variant="outline" onClick={copyToClipboard}>Copia</Button>
                </div>
              </div>
            )}
            
            <div className="flex flex-col gap-2">
              <Button 
                onClick={handleSendApprovalEmail} 
                className="btn-primary w-full"
                disabled={sendingApproval}
              >
                {sendingApproval ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Invio...</>
                ) : (
                  <><Mail className="w-4 h-4 mr-2" />Invia Email al Cliente</>
                )}
              </Button>
              <Button 
                onClick={handleGenerateApprovalLink} 
                variant="outline"
                disabled={sendingApproval}
              >
                Genera Solo Link
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Dettaglio Rapporto */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1B6B93] flex items-center gap-2">
              <FileText className="w-5 h-5" /> Dettaglio Rapporto {selectedReportDetail?.report_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedReportDetail && (
            <div className="space-y-4">
              {/* Informazioni Base */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <CalendarDays className="w-4 h-4" /> Informazioni Generali
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500">Data:</span>
                    <span className="ml-2 font-medium">
                      {selectedReportDetail.is_multi_day && selectedReportDetail.date_range 
                        ? selectedReportDetail.date_range.replace(/(\d{4})-(\d{2})-(\d{2})/g, (m, y, mo, d) => `${d}/${mo}/${y}`)
                        : formatDateIT(selectedReportDetail.date)}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Orario:</span>
                    <span className="ml-2 font-medium">{selectedReportDetail.start_time} - {selectedReportDetail.end_time}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Cantiere:</span>
                    <span className="ml-2 font-medium">{selectedReportDetail.site_name}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Cliente:</span>
                    <span className="ml-2 font-medium">{sites.find(s => s.id === selectedReportDetail.site_id)?.client_name || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Attività:</span>
                    <span className="ml-2 font-medium">{selectedReportDetail.activity_type}</span>
                  </div>
                  {selectedReportDetail.ticket && (
                    <div>
                      <span className="text-slate-500">Rif. Ticket:</span>
                      <span className="ml-2 font-medium">{selectedReportDetail.ticket}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Ore */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Ore Lavorate
                </h3>
                <div className="flex flex-wrap gap-3">
                  <div className="bg-white rounded-lg px-4 py-2 shadow-sm">
                    <div className="text-xs text-slate-500">Totali</div>
                    <div className="text-xl font-bold text-[#1B6B93]">h {formatHours(selectedReportDetail.total_hours)}</div>
                  </div>
                  {(() => {
                    const diurne = (selectedReportDetail.total_hours || 0) - (selectedReportDetail.night_hours || 0) - (selectedReportDetail.semifestive_hours || 0) - (selectedReportDetail.festive_hours || 0);
                    // Mostra ore diurne solo se significative (>0.01h = 36 secondi)
                    return diurne > 0.01 && (
                      <div className="bg-white rounded-lg px-4 py-2 shadow-sm">
                        <div className="text-xs text-slate-500">Diurne</div>
                        <div className="text-lg font-bold text-slate-700">h {formatHours(diurne)}</div>
                      </div>
                    );
                  })()}
                  {selectedReportDetail.night_hours > 0 && (
                    <div className="bg-indigo-100 rounded-lg px-4 py-2 shadow-sm">
                      <div className="text-xs text-indigo-600">🌙 Notturne</div>
                      <div className="text-lg font-bold text-indigo-700">h {formatHours(selectedReportDetail.night_hours)}</div>
                    </div>
                  )}
                  {selectedReportDetail.semifestive_hours > 0 && (
                    <div className="bg-orange-100 rounded-lg px-4 py-2 shadow-sm">
                      <div className="text-xs text-orange-600">📅 Semifestive</div>
                      <div className="text-lg font-bold text-orange-700">h {formatHours(selectedReportDetail.semifestive_hours)}</div>
                    </div>
                  )}
                  {selectedReportDetail.festive_hours > 0 && (
                    <div className="bg-red-100 rounded-lg px-4 py-2 shadow-sm">
                      <div className="text-xs text-red-600">🎉 Festive</div>
                      <div className="text-lg font-bold text-red-700">h {formatHours(selectedReportDetail.festive_hours)}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Dipendenti Coinvolti */}
              <div className="bg-emerald-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Dipendenti Coinvolti ({selectedReportDetail.is_multi_day ? selectedReportDetail.num_employees : selectedReportDetail.employees?.length || 0})
                </h3>
                {selectedReportDetail.is_multi_day && selectedReportDetail.workdays ? (
                  <div className="space-y-2">
                    {selectedReportDetail.workdays.map((wd, idx) => (
                      <div key={idx} className="bg-white rounded p-2 text-sm">
                        <div className="font-medium text-slate-600 mb-1">{formatDateIT(wd.date)} ({wd.start_time}-{wd.end_time})</div>
                        <div className="flex flex-wrap gap-1">
                          {wd.employees?.map((emp, i) => (
                            <span key={i} className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-xs">
                              {emp.employee_name} (h {formatHours(emp.hours)})
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {selectedReportDetail.employees?.map((emp, idx) => (
                      <div key={idx} className="bg-white rounded-lg px-3 py-2 shadow-sm text-sm">
                        <div className="font-medium">{emp.employee_name}</div>
                        <div className="text-xs text-slate-500">
                          {emp.start_time && emp.end_time ? `${emp.start_time}-${emp.end_time}` : `${selectedReportDetail.start_time}-${selectedReportDetail.end_time}`} • h {formatHours(emp.hours)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Note */}
              {selectedReportDetail.notes && (
                <div className="bg-amber-50 rounded-lg p-4">
                  <h3 className="font-semibold text-slate-700 mb-2">📝 Note</h3>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{selectedReportDetail.notes}</p>
                </div>
              )}

              {/* Foto Allegate */}
              <div className="bg-blue-50 rounded-lg p-4">
                <PhotoUpload
                  reportId={selectedReportDetail.id}
                  photos={selectedReportDetail.photos || []}
                  onPhotosChange={(newPhotos) => {
                    setSelectedReportDetail({ ...selectedReportDetail, photos: newPhotos });
                    // Update also in reports list
                    setReports(reports.map(r => r.id === selectedReportDetail.id ? { ...r, photos: newPhotos } : r));
                  }}
                />
              </div>

              {/* Attrezzature */}
              {selectedReportDetail.equipment && selectedReportDetail.equipment.length > 0 && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <h3 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Wrench className="w-4 h-4" /> Attrezzature Utilizzate
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedReportDetail.equipment.map((eq, idx) => (
                      <span key={idx} className="bg-white text-purple-700 px-3 py-1 rounded-lg text-sm shadow-sm">
                        {eq.equipment_name}: h {formatHours(eq.hours)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Stato */}
              <div className="bg-slate-100 rounded-lg p-4">
                <h3 className="font-semibold text-slate-700 mb-3">📋 Stato</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${selectedReportDetail.employee_signature ? 'bg-green-500' : 'bg-red-400'}`}></span>
                    <span>Firma Dipendente</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${selectedReportDetail.client_signature || selectedReportDetail.client_approved_at ? 'bg-green-500' : 'bg-red-400'}`}></span>
                    <span>Firma/Approv. Cliente</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${selectedReportDetail.email_sent ? 'bg-green-500' : 'bg-slate-300'}`}></span>
                    <span>Email Inviata</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(selectedReportDetail.status)}
                  </div>
                </div>
                {selectedReportDetail.client_approved_at && (
                  <p className="text-xs text-emerald-600 mt-2">
                    ✓ Approvato online il {new Date(selectedReportDetail.client_approved_at).toLocaleDateString('it-IT')} alle {new Date(selectedReportDetail.client_approved_at).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}
                  </p>
                )}
              </div>

              {/* Integrazione HR (solo Admin/Gestore) */}
              {isAdminOrGestore() && (
                <div className="bg-indigo-50 rounded-lg p-4" data-testid="hr-sync-section">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                      🔗 Portale HR
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendToHr(selectedReportDetail.id)}
                      disabled={sendingToHr}
                      className="text-indigo-600 border-indigo-300 hover:bg-indigo-100"
                      data-testid="send-to-hr-btn"
                    >
                      {sendingToHr ? 'Invio...' : (selectedReportDetail.hr_sync ? 'Reinvia a HR' : 'Invia a HR')}
                    </Button>
                  </div>
                  {selectedReportDetail.hr_sync ? (
                    <div className="text-sm space-y-1" data-testid="hr-sync-status">
                      <p className={selectedReportDetail.hr_sync.success ? 'text-emerald-700' : 'text-red-600'}>
                        {selectedReportDetail.hr_sync.success ? '✅ Consuntivi inviati' : '❌ Invio con errori'}
                        {selectedReportDetail.hr_sync.last_sent_at && (
                          <span className="text-slate-500"> — {new Date(selectedReportDetail.hr_sync.last_sent_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        )}
                      </p>
                      {(selectedReportDetail.hr_sync.items || []).map((it, i) => (
                        <p key={i} className="text-xs text-slate-600">
                          {it.status === 'success' ? '✅' : '❌'} Matricola {it.matricola} — {it.date} — h {it.hours}
                          {it.error && <span className="text-red-500"> ({it.error})</span>}
                        </p>
                      ))}
                      {selectedReportDetail.hr_sync.error && (
                        <p className="text-xs text-red-500">{selectedReportDetail.hr_sync.error}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">Consuntivi non ancora inviati al portale HR (invio automatico all'approvazione)</p>
                  )}
                </div>
              )}

              {/* Storico Solleciti (solo Admin/Gestore) */}
              {isAdminOrGestore() && reminderHistory && (
                <div className="bg-amber-50 rounded-lg p-4" data-testid="reminders-section">
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2 mb-2">
                    ⏰ Solleciti Approvazione
                  </h3>
                  {(reminderHistory.reminders || []).length > 0 ? (
                    <div className="text-sm space-y-1" data-testid="reminders-list">
                      <p className="text-slate-600">
                        <strong>{reminderHistory.count}</strong> {reminderHistory.count === 1 ? 'sollecito inviato' : 'solleciti inviati'} al cliente
                      </p>
                      {reminderHistory.reminders.map((r, i) => (
                        <p key={i} className="text-xs text-slate-600">
                          {r.status === 'success' ? '✅' : '❌'} {new Date(r.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          {r.recipients?.length > 0 && <span className="text-slate-400"> → {r.recipients.join(', ')}</span>}
                          {r.error_message && <span className="text-red-500"> ({r.error_message})</span>}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500" data-testid="reminders-empty">
                      Nessun sollecito inviato{reminderHistory.reminder_skipped ? ` (saltato: ${reminderHistory.reminder_skipped})` : ''}. Il promemoria parte in automatico dopo 3 giorni senza approvazione.
                    </p>
                  )}
                </div>
              )}

              {/* Diagnostica Email (solo Admin) */}
              {user?.role === 'admin' && (
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                      🔍 Diagnostica Email
                    </h3>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleDiagnoseReport(selectedReportDetail.id)}
                      disabled={loadingDiagnosis}
                      className="text-orange-600 border-orange-300 hover:bg-orange-100"
                    >
                      {loadingDiagnosis ? 'Analisi...' : 'Analizza'}
                    </Button>
                  </div>
                  
                  {showDiagnosis && diagnosisData && (
                    <div className="space-y-3 text-sm">
                      <div className="grid grid-cols-2 gap-2 bg-white rounded p-3">
                        <div><span className="text-slate-500">Email Cantiere:</span></div>
                        <div className="font-medium">{diagnosisData.site_client_email || '❌ Non configurata'}</div>
                        <div><span className="text-slate-500">Email Rapporto:</span></div>
                        <div className="font-medium">{diagnosisData.report_client_email || '❌ Non presente'}</div>
                        <div><span className="text-slate-500">Firma Dipendente:</span></div>
                        <div>{diagnosisData.has_employee_signature ? '✅ Presente' : '❌ Mancante'}</div>
                        <div><span className="text-slate-500">Firma Cliente:</span></div>
                        <div>{diagnosisData.has_client_signature ? '✅ Presente' : '⚠️ Mancante'}</div>
                        <div><span className="text-slate-500">Skip Email:</span></div>
                        <div>{diagnosisData.skip_email_send ? '⚠️ Attivo' : '✅ No'}</div>
                        <div><span className="text-slate-500">Email Inviata:</span></div>
                        <div>{diagnosisData.email_sent ? '✅ Sì' : '❌ No'}</div>
                        <div><span className="text-slate-500">Richiesta Approvazione:</span></div>
                        <div data-testid="diagnosis-approval-status">{diagnosisData.approval_request_sent ? '✅ Inviata' : (diagnosisData.approval_tokens?.length > 0 ? '⚠️ Link generato (invio non tracciato)' : '❌ Non inviata')}</div>
                      </div>
                      
                      <div className="bg-white rounded p-3">
                        <div className="font-medium text-slate-700 mb-2">Diagnosi:</div>
                        <ul className="space-y-1">
                          {diagnosisData.diagnosis?.map((reason, idx) => (
                            <li key={idx} className="text-slate-600">{reason}</li>
                          ))}
                        </ul>
                      </div>
                      
                      {diagnosisData.email_logs && diagnosisData.email_logs.length > 0 && (
                        <div className="bg-white rounded p-3">
                          <div className="font-medium text-slate-700 mb-2">Log Email:</div>
                          {diagnosisData.email_logs.map((log, idx) => (
                            <div key={idx} className="text-xs border-b pb-1 mb-1 last:border-0">
                              <span className={log.status === 'success' ? 'text-green-600' : 'text-red-600'}>
                                {log.status === 'success' ? '✅' : '❌'} {log.email_type}
                              </span>
                              <span className="text-slate-400 ml-2">
                                {new Date(log.created_at).toLocaleString('it-IT')}
                              </span>
                              {log.error_message && <div className="text-red-500 mt-1">{log.error_message}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {diagnosisData.email_would_be_sent_to?.length > 0 && (
                        <div className="text-xs text-slate-500 mb-2">
                          📧 Email verrebbe inviata a: {diagnosisData.email_would_be_sent_to.join(', ')}
                        </div>
                      )}
                      
                      {/* Pulsante Riprova Invio */}
                      {!diagnosisData.email_sent && diagnosisData.has_employee_signature && diagnosisData.has_client_signature && diagnosisData.report_client_email && (
                        <Button 
                          size="sm" 
                          className="w-full bg-orange-500 hover:bg-orange-600"
                          onClick={() => handleRetryEmail(selectedReportDetail.id)}
                        >
                          🔄 Riprova Invio Email (con log dettagliato)
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Azioni */}
              <div className="flex justify-end gap-3 pt-2 border-t">
                <Button variant="outline" onClick={() => setIsDetailDialogOpen(false)}>
                  Chiudi
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="text-blue-600">
                      <FileDown className="w-4 h-4 mr-2" /> Scarica PDF
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => handleDownloadPDF(selectedReportDetail.id, false)}>PDF con ore</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDownloadPDF(selectedReportDetail.id, true)}>PDF senza ore</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {isAdminOrGestore() && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className={selectedReportDetail.email_sent ? "bg-orange-500 hover:bg-orange-600" : "bg-emerald-600 hover:bg-emerald-700"}>
                        <Mail className="w-4 h-4 mr-2" /> {selectedReportDetail.email_sent ? "Reinvia Email" : "Invia Email"}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => { setIsDetailDialogOpen(false); openEmailDialog(selectedReportDetail, false); }}>Con ore</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { setIsDetailDialogOpen(false); openEmailDialog(selectedReportDetail, true); }}>Senza ore</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Visualizzazione Foto */}
      <Dialog open={!!viewingPhotosReport} onOpenChange={(open) => !open && setViewingPhotosReport(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Camera className="w-4 h-4 text-[#1B6B93]" />
              Foto Rapporto {viewingPhotosReport?.report_number ? `n. ${viewingPhotosReport.report_number}` : ''}
              <span className="ml-auto text-sm font-normal text-slate-500">
                {viewingPhotosReport?.photos?.length || 0} foto
              </span>
            </DialogTitle>
          </DialogHeader>
          
          {viewingPhotosReport?.photos && viewingPhotosReport.photos.length > 0 ? (
            <div className="space-y-3">
              {/* Griglia foto - layout compatto */}
              <div className={`grid gap-2 ${
                viewingPhotosReport.photos.length === 1 ? 'grid-cols-1' :
                viewingPhotosReport.photos.length === 2 ? 'grid-cols-2' :
                viewingPhotosReport.photos.length <= 4 ? 'grid-cols-2' :
                'grid-cols-3'
              }`}>
                {viewingPhotosReport.photos.map((photo, idx) => (
                  <div 
                    key={idx}
                    className={`relative bg-slate-100 rounded-lg overflow-hidden cursor-pointer group ${
                      viewingPhotosReport.photos.length === 1 ? 'aspect-video' : 'aspect-square'
                    }`}
                    onClick={() => photo?.filename && window.open(`${API}/photos/${photo.filename}`, '_blank')}
                  >
                    {photo?.filename ? (
                      <>
                        <img 
                          src={`${API}/photos/${photo.filename}`}
                          alt={`Foto ${idx + 1}`}
                          className="w-full h-full object-cover transition-transform group-hover:scale-105"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23fef3c7" width="100" height="100"/><text fill="%23d97706" x="50" y="55" text-anchor="middle" font-size="12">⚠️</text></svg>';
                          }}
                        />
                        {/* Overlay con numero e zoom */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2">
                            <ZoomIn className="w-6 h-6 text-white drop-shadow-lg" />
                          </div>
                        </div>
                        {/* Badge numero foto */}
                        <div className="absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full">
                          {idx + 1}
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        <Camera className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Info rapporto - compatta */}
              <div className="flex justify-between items-center text-xs text-slate-500 pt-2 border-t">
                <div className="flex items-center gap-1">
                  <span className="font-medium text-slate-700">{viewingPhotosReport.site_name}</span>
                  {viewingPhotosReport.activity_type && (
                    <span className="text-slate-400">• {viewingPhotosReport.activity_type}</span>
                  )}
                </div>
                <span>{formatDateIT(viewingPhotosReport.date || viewingPhotosReport.start_date)}</span>
              </div>
              
              {/* Hint */}
              <p className="text-xs text-center text-slate-400">
                Clicca su una foto per aprirla a grandezza originale
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-slate-400">
              <Camera className="w-10 h-10 mb-2" />
              <p className="text-sm">Nessuna foto allegata</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Esportazione Report */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1B6B93]">Esporta Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Tipo report */}
            <div>
              <Label className="text-sm font-medium">Tipo Report</Label>
              <div className="flex gap-2 mt-1">
                <Button
                  type="button"
                  variant={exportType === 'fatture' ? 'default' : 'outline'}
                  className={exportType === 'fatture' ? 'bg-blue-600 hover:bg-blue-700 flex-1' : 'flex-1'}
                  onClick={() => setExportType('fatture')}
                >
                  <Building2 className="w-4 h-4 mr-2" /> Fatture
                </Button>
                <Button
                  type="button"
                  variant={exportType === 'paghe' ? 'default' : 'outline'}
                  className={exportType === 'paghe' ? 'bg-green-600 hover:bg-green-700 flex-1' : 'flex-1'}
                  onClick={() => setExportType('paghe')}
                >
                  <Users className="w-4 h-4 mr-2" /> Paghe
                </Button>
              </div>
            </div>

            {/* Cliente */}
            <div>
              <Label className="text-sm font-medium">Cliente</Label>
              <select
                className="w-full mt-1 p-2 border rounded-lg text-sm"
                value={exportFilters.client_name}
                onChange={(e) => setExportFilters({ ...exportFilters, client_name: e.target.value })}
              >
                <option value="">Tutti i clienti</option>
                {uniqueClients.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {/* Cantiere */}
            <div>
              <Label className="text-sm font-medium">Cantiere</Label>
              <select
                className="w-full mt-1 p-2 border rounded-lg text-sm"
                value={exportFilters.site_id}
                onChange={(e) => setExportFilters({ ...exportFilters, site_id: e.target.value })}
              >
                <option value="">Tutti i cantieri</option>
                {sites
                  .filter(s => !exportFilters.client_name || s.client_name === exportFilters.client_name)
                  .map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            {/* Filtra per tipo data */}
            <div>
              <Label className="text-sm font-medium">Filtra per</Label>
              <div className="flex gap-2 mt-1">
                <Button
                  type="button"
                  size="sm"
                  variant={exportFilters.date_filter_by === 'intervention' ? 'default' : 'outline'}
                  className={exportFilters.date_filter_by === 'intervention' ? 'bg-[#1B6B93] hover:bg-[#155a7a] flex-1 text-xs' : 'flex-1 text-xs'}
                  onClick={() => setExportFilters({ ...exportFilters, date_filter_by: 'intervention' })}
                  data-testid="filter-by-intervention"
                >
                  Data Intervento
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={exportFilters.date_filter_by === 'report' ? 'default' : 'outline'}
                  className={exportFilters.date_filter_by === 'report' ? 'bg-[#1B6B93] hover:bg-[#155a7a] flex-1 text-xs' : 'flex-1 text-xs'}
                  onClick={() => setExportFilters({ ...exportFilters, date_filter_by: 'report' })}
                  data-testid="filter-by-report-date"
                >
                  Data Rapporto
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {exportFilters.date_filter_by === 'intervention' 
                  ? 'Filtra in base alla data dell\'intervento effettuato' 
                  : 'Filtra in base alla data di creazione del rapporto'}
              </p>
            </div>

            {/* Range date */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Data da</Label>
                <Input
                  type="date"
                  value={exportFilters.date_from}
                  onChange={(e) => setExportFilters({ ...exportFilters, date_from: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Data a</Label>
                <Input
                  type="date"
                  value={exportFilters.date_to}
                  onChange={(e) => setExportFilters({ ...exportFilters, date_to: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Ordinamento */}
            <div>
              <Label className="text-sm font-medium">Ordina per</Label>
              <select
                className="w-full mt-1 p-2 border rounded-lg text-sm"
                value={exportFilters.sort_by}
                onChange={(e) => setExportFilters({ ...exportFilters, sort_by: e.target.value })}
              >
                <option value="date">Data</option>
                <option value="report_number">N. Rapporto</option>
                <option value="site">Cantiere</option>
                {exportType === 'fatture' && <option value="client">Cliente</option>}
                {exportType === 'paghe' && <option value="employee">Dipendente</option>}
              </select>
            </div>

            {/* Pulsanti */}
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button variant="outline" onClick={() => setExportDialogOpen(false)}>Annulla</Button>
              <Button
                className={exportType === 'fatture' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}
                onClick={() => handleExportPDF()}
              >
                <Download className="w-4 h-4 mr-2" />
                Esporta {exportType === 'fatture' ? 'Fatture' : 'Paghe'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsPage;
